/**
 * 이 파일은 svg icon을 생성하는 파일입니다.
 * 현재는 오브젝트에 담아서 키값을 호출에 리턴하는 방식입니다.
 * hover, active 등의 색상은 부모의 style로 설정하려고 합니다.
 * 추후에 방법이 변경될 수 있습니다.
 */
import React, {ReactElement} from 'react'
import styled from 'styled-components'
import Colors from '../../theme/Colors'
import PersonFill from '../../assets/svgs/PersonFill.svg'
import EmailFill from '../../assets/svgs/EmailFill.svg'
import ResetFill from '../../assets/svgs/ResetFill.svg'

interface IconWrapType {
  iconColor: string
}

// 혹시 모를 차이를 두기 위해 변수명 앞에 Styled를 붙이지 않았습니다.
export const IconWrap = styled.span<IconWrapType>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;

  svg * {
    fill: ${(props) => props.iconColor};
    stroke: ${(props) => props.iconColor};
    transition: fill 300ms linear, stroke 300ms linear;
  }

  transition: transform 0.34s linear;

  pointer-events: none;
`

interface SvgIconType {
  name: string
  iconColor: string
}
/**
 * not pure Svg icon & include image icon case
 */
const PersonFillImg = () => <img src={PersonFill} alt='' />
const ResetFillImg = () => <img src={ResetFill} alt='' />
const EmailFillImg = () => <img src={EmailFill} alt='' />

/**
 * - svg icon 호출하는 컴포넌트
 * ****
 * @ex `<SvgIcon iconColor='white' name='dashboardFill' />`  */
export const SvgIcon = (props: SvgIconType) => {
  const {name, iconColor} = props

  /**
   * - IconWrap에 svg 태그를 추가하는 오브젝트
   * - 새로운 아이콘이 필요할 경우 추가해서 사용
   * ***
   * @MUSTDO
   * - svg tag 추가시 fill, stroke가 투명해야하는 rect, circle, d 등의 태그에 inline style로 fill: none, stroke: none 추가
   * - fill, stroke를 css로 제어하면 안되는 경우 inline style로 fill: '색상명', stroke: '색상명' 추가
   * - style={{fill: 'none'}} or style={{stroke: 'none'}} or style={{stroke: '#aaaaaa'}}
   * ***
   * @key name
   * @value IconWrap > svg tag
   * */
  const svgIcons: {[key: string]: ReactElement<any, any> | null} = {
    refresh: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          viewBox='0 0 24 24'
        >
          <path
            d='M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z'
            style={{stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    success: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          viewBox='0 0 24 24'
        >
          <path
            d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'
            style={{stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    fail: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          viewBox='0 0 24 24'
        >
          <path
            d='M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z'
            style={{stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    inquiry: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='19'
          height='19'
          viewBox='0 0 19 19'
          fill='none'
        >
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M9.5 19.001C4.25329 19.001 0 14.7477 0 9.50098C0 4.25427 4.25329 0.000976562 9.5 0.000976562C14.7467 0.000976562 19 4.25427 19 9.50098C19 10.9959 18.6547 12.4102 18.0395 13.6685L18.9158 18.9135L13.5647 18.0899C12.3325 18.6742 10.9544 19.001 9.5 19.001Z'
            fill='#353E56'
          />
        </svg>
      </IconWrap>
    ),
    clock: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='none'
        >
          <path d='M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z' />
          <path d='M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z' />
        </svg>
      </IconWrap>
    ),
    clockSmall: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='15'
          height='15'
          viewBox='0 0 24 24'
          fill='none'
        >
          <path d='M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z' />
          <path d='M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z' />
        </svg>
      </IconWrap>
    ),
    close: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='23'
          height='24'
          viewBox='0 0 23 24'
          fill='none'
        >
          <line
            x1='0.424264'
            y1='0.575736'
            x2='22.4243'
            y2='22.5757'
            stroke='#BABABE'
            strokeWidth='1.2'
            style={{
              fill: 'none',
            }}
          />
          <line
            x1='22.4243'
            y1='1.42426'
            x2='0.424265'
            y2='23.4243'
            stroke='#BABABE'
            strokeWidth='1.2'
            style={{
              fill: 'none',
            }}
          />
        </svg>
      </IconWrap>
    ),
    counts: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='12'
          height='9'
          viewBox='0 0 12 9'
          fill='none'
        >
          <path
            d='M0.889738 0.548708V2H1.39995V0H0.954384L0.199951 0.632634L0.49086 0.887598L0.889738 0.548708Z'
            fill='#DDDDDD'
            style={{stroke: 'none'}}
          />
          <path
            d='M1.0058 4.06951C1.21846 4.06951 1.42363 4.15219 1.42363 4.32659C1.42363 4.56873 1.11431 4.7697 0.0582405 5.36636V5.69727H2V5.32798H0.947801C1.61721 4.95573 1.98067 4.6634 1.98067 4.32659C1.98067 3.93368 1.54761 3.69727 0.9942 3.69727C0.634364 3.69727 0.251571 3.79766 0 4.01931L0.313195 4.29706C0.487433 4.15809 0.711697 4.06951 1.0058 4.06951Z'
            fill='#DDDDDD'
            style={{stroke: 'none'}}
          />
          <path
            d='M11.5468 0.779297H3.23497C2.98454 0.779297 2.78162 0.982222 2.78162 1.23265V1.65088C2.78162 1.90117 2.98454 2.10423 3.23497 2.10423H11.5466C11.7969 2.10423 12 1.90131 12 1.65088V1.23265C12.0001 0.982355 11.7971 0.779297 11.5468 0.779297Z'
            fill='#DDDDDD'
            style={{stroke: 'none'}}
          />
          <path
            d='M11.5468 4.0918H3.23497C2.98454 4.0918 2.78162 4.29472 2.78162 4.54515V4.96338C2.78162 5.21367 2.98454 5.41673 3.23497 5.41673H11.5466C11.7969 5.41673 12 5.21381 12 4.96338V4.54515C12.0001 4.29472 11.7971 4.0918 11.5468 4.0918Z'
            fill='#DDDDDD'
            style={{stroke: 'none'}}
          />
          <path
            d='M11.5468 7.4043H3.23497C2.98454 7.4043 2.78162 7.60722 2.78162 7.85765V8.27588C2.78162 8.52617 2.98454 8.72923 3.23497 8.72923H11.5466C11.7969 8.72923 12 8.52631 12 8.27588V7.85765C12.0001 7.60722 11.7971 7.4043 11.5468 7.4043Z'
            fill='#DDDDDD'
            style={{stroke: 'none'}}
          />
          <path
            d='M1.40706 7.96654C1.68832 7.92882 1.96223 7.76925 1.96223 7.52534C1.96223 7.20328 1.5971 7 1.00392 7C0.558974 7 0.239696 7.13056 0.0342083 7.30773L0.304074 7.56596C0.482718 7.4354 0.703409 7.36576 0.950469 7.36576C1.21273 7.36576 1.42202 7.4412 1.42202 7.59498C1.42202 7.73733 1.23197 7.80116 0.950469 7.80116C0.855446 7.80116 0.680366 7.80116 0.634754 7.79826V8.16983C0.672764 8.16692 0.843805 8.16402 0.950469 8.16402C1.30419 8.16402 1.46003 8.23366 1.46003 8.38761C1.46003 8.53269 1.28875 8.63424 0.980876 8.63424C0.733579 8.63424 0.456111 8.553 0.285069 8.41373L0 8.68955C0.186245 8.86363 0.536168 9 0.999881 9C1.60827 9 2 8.76498 2 8.43114C2.00047 8.14063 1.66576 7.98685 1.40706 7.96654Z'
            fill='#DDDDDD'
            style={{stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    paymentRepeat: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='19'
          height='14'
          viewBox='0 0 19 14'
          fill='none'
        >
          <path
            d='M13.1297 5.83365H17.7187C17.7742 5.83367 17.8285 5.84949 17.8753 5.87925C17.922 5.909 17.9594 5.95148 17.9829 6.00168C18.0064 6.05189 18.0151 6.10776 18.008 6.16275C18.0009 6.21773 17.9783 6.26956 17.9428 6.31216L15.6483 9.06651C15.6209 9.09934 15.5867 9.12576 15.5479 9.14389C15.5092 9.16202 15.467 9.17141 15.4242 9.17141C15.3815 9.17141 15.3392 9.16202 15.3005 9.14389C15.2618 9.12576 15.2275 9.09934 15.2002 9.06651L12.9056 6.31216C12.8702 6.26956 12.8475 6.21773 12.8405 6.16275C12.8334 6.10776 12.8421 6.05189 12.8656 6.00168C12.8891 5.95148 12.9264 5.909 12.9732 5.87925C13.02 5.84949 13.0743 5.83367 13.1297 5.83365ZM0.291665 8.16784H4.88069C4.93613 8.16782 4.99042 8.152 5.03719 8.12225C5.08397 8.09249 5.1213 8.05002 5.14481 7.99981C5.16833 7.9496 5.17705 7.89373 5.16996 7.83874C5.16286 7.78376 5.14025 7.73193 5.10477 7.68933L2.81026 4.93499C2.78288 4.90215 2.74861 4.87573 2.70989 4.8576C2.67117 4.83947 2.62893 4.83008 2.58618 4.83008C2.54342 4.83008 2.50119 4.83947 2.46246 4.8576C2.42374 4.87573 2.38948 4.90215 2.36209 4.93499L0.0675824 7.68933C0.0321 7.73193 0.00948901 7.78376 0.00239714 7.83874C-0.00469474 7.89373 0.00402605 7.9496 0.0275384 7.99981C0.0510507 8.05002 0.0883812 8.09249 0.135159 8.12225C0.181936 8.152 0.236224 8.16782 0.291665 8.16784Z'
            fill='#DDDDDD'
            style={{stroke: 'none'}}
          />
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M9.0052 1.16452C7.19386 1.16452 5.57393 1.98966 4.50371 3.2863C4.45592 3.34824 4.39616 3.39994 4.32799 3.43832C4.25982 3.47671 4.18463 3.50099 4.10688 3.50973C4.02914 3.51847 3.95043 3.51149 3.87544 3.48921C3.80044 3.46692 3.7307 3.42978 3.67035 3.37999C3.61 3.33021 3.56027 3.2688 3.52413 3.19941C3.48799 3.13003 3.46617 3.05408 3.45997 2.9761C3.45377 2.89811 3.46331 2.81967 3.48803 2.74544C3.51276 2.67122 3.55215 2.60272 3.60387 2.54403C4.45841 1.50943 5.59213 0.742043 6.87018 0.333144C8.14824 -0.0757544 9.51685 -0.108958 10.8132 0.237481C12.1096 0.583921 13.2792 1.29542 14.1829 2.28737C15.0866 3.27931 15.6864 4.50994 15.9109 5.8329H14.724C14.4545 4.51503 13.7382 3.33064 12.6962 2.48001C11.6542 1.62937 10.3503 1.16468 9.0052 1.16452ZM3.28642 8.1671C3.50737 9.24622 4.02911 10.2408 4.79137 11.036C5.55363 11.8311 6.5253 12.3944 7.59413 12.6607C8.66296 12.9271 9.78533 12.8856 10.8316 12.541C11.8778 12.1965 12.8052 11.563 13.5067 10.7137C13.5545 10.6518 13.6142 10.6001 13.6824 10.5617C13.7506 10.5233 13.8258 10.499 13.9035 10.4903C13.9813 10.4815 14.06 10.4885 14.135 10.5108C14.2099 10.5331 14.2797 10.5702 14.34 10.62C14.4004 10.6698 14.4501 10.7312 14.4863 10.8006C14.5224 10.87 14.5442 10.9459 14.5504 11.0239C14.5566 11.1019 14.5471 11.1803 14.5224 11.2546C14.4976 11.3288 14.4582 11.3973 14.4065 11.456C13.552 12.4906 12.4183 13.258 11.1402 13.6669C9.86215 14.0758 8.49354 14.109 7.19716 13.7625C5.90078 13.4161 4.73118 12.7046 3.82748 11.7126C2.92378 10.7207 2.324 9.49006 2.09949 8.1671H3.28642Z'
            fill='#DDDDDD'
            style={{stroke: 'none'}}
          />
          <path
            d='M10.4437 8.45021L10.3106 9.51613H10.2696L10.0956 8.45021L9.93174 7.62272H10.5666L10.4437 8.45021ZM8.76451 7.29313L8.80546 7.08275L8.98976 5.80645H9.04096L9.22526 7.06872L9.26621 7.29313H8.76451ZM7.99659 8.45021L7.83276 9.51613H7.79181L7.66894 8.45021L7.54607 7.62272H8.16041L7.99659 8.45021ZM12 7.29313H11.314L11.7338 5H10.9352L10.6075 7.29313H9.86007L9.46075 5.31557H8.6314L8.23208 7.29313H7.50512L7.16724 5H6.29693L6.74744 7.30014L6 7.32819V7.62272H6.80887L7.27986 10H8.24232L8.70307 7.62272H9.33788L9.80887 10H10.8225L11.2526 7.62272H12V7.29313Z'
            fill='#D0D0D0'
            style={{stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    search: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='14'
          height='15'
          viewBox='0 0 14 15'
          fill='none'
        >
          <path
            d='M13.77 12.8085L11.1264 10.1727C11.9794 9.08605 12.4422 7.7442 12.4404 6.3628C12.4404 5.13256 12.0756 3.92994 11.3921 2.90703C10.7087 1.88412 9.73719 1.08686 8.60059 0.616067C7.464 0.145273 6.21332 0.0220922 5.00672 0.262101C3.80011 0.502109 2.69178 1.09453 1.82186 1.96444C0.951949 2.83435 0.359531 3.94269 0.119523 5.14929C-0.120486 6.3559 0.0026951 7.60658 0.473489 8.74317C0.944282 9.87977 1.74154 10.8512 2.76445 11.5347C3.78736 12.2182 4.98998 12.583 6.22022 12.583C7.60162 12.5847 8.94347 12.1219 10.0301 11.269L12.6659 13.9126C12.7382 13.9855 12.8242 14.0433 12.9189 14.0828C13.0137 14.1223 13.1153 14.1426 13.218 14.1426C13.3206 14.1426 13.4222 14.1223 13.517 14.0828C13.6117 14.0433 13.6977 13.9855 13.77 13.9126C13.8429 13.8403 13.9007 13.7543 13.9402 13.6596C13.9797 13.5648 14 13.4632 14 13.3605C14 13.2579 13.9797 13.1563 13.9402 13.0615C13.9007 12.9668 13.8429 12.8808 13.77 12.8085ZM1.55506 6.3628C1.55506 5.44012 1.82866 4.53816 2.34128 3.77097C2.85389 3.00379 3.58249 2.40584 4.43494 2.05275C5.28739 1.69965 6.2254 1.60727 7.13035 1.78727C8.0353 1.96728 8.86655 2.4116 9.51899 3.06403C10.1714 3.71646 10.6157 4.54772 10.7957 5.45267C10.9757 6.35762 10.8834 7.29563 10.5303 8.14808C10.1772 9.00053 9.57923 9.72912 8.81205 10.2417C8.04486 10.7544 7.1429 11.028 6.22022 11.028C4.98294 11.028 3.79634 10.5365 2.92145 9.66157C2.04656 8.78668 1.55506 7.60008 1.55506 6.3628Z'
            fill='#676A6C'
            style={{stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    cart: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='14'
          height='15'
          viewBox='0 0 14 15'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M4.22375 14.248C3.87194 14.248 3.57202 14.1227 3.32401 13.8722C3.07599 13.6217 2.95198 13.3205 2.95198 12.9687C2.95198 12.6169 3.07601 12.317 3.32408 12.069C3.57215 11.8209 3.87037 11.6969 4.21872 11.6969C4.56708 11.6969 4.86822 11.8222 5.12214 12.0727C5.37607 12.3233 5.50303 12.6244 5.50303 12.9762C5.50303 13.328 5.37777 13.6279 5.12725 13.876C4.87671 14.124 4.57555 14.248 4.22375 14.248ZM11.31 14.248C10.9582 14.248 10.6583 14.1227 10.4103 13.8722C10.1622 13.6217 10.0382 13.3205 10.0382 12.9687C10.0382 12.6169 10.1623 12.317 10.4103 12.069C10.6584 11.8209 10.9566 11.6969 11.305 11.6969C11.6533 11.6969 11.9545 11.8222 12.2084 12.0727C12.4623 12.3233 12.5893 12.6244 12.5893 12.9762C12.5893 13.328 12.464 13.6279 12.2135 13.876C11.963 14.124 11.6618 14.248 11.31 14.248ZM4.05035 10.5631C3.53172 10.5631 3.15892 10.3893 2.93196 10.0417C2.70499 9.69405 2.70935 9.30649 2.94505 8.879L4.06613 6.81397L1.39877 1.15612H0.560364C0.404513 1.15612 0.27216 1.09901 0.163303 0.984778C0.0544343 0.870535 0 0.732702 0 0.571277C0 0.409864 0.0554028 0.274297 0.166208 0.164579C0.277002 0.0548596 0.414292 0 0.57808 0H1.76579C1.88621 0 1.99557 0.0295617 2.09388 0.0886848C2.19219 0.14782 2.26345 0.231355 2.30766 0.33929L2.80448 1.39954H13.1161C13.5562 1.39954 13.8307 1.5274 13.9395 1.78312C14.0484 2.03884 14.0103 2.33101 13.8252 2.65965L11.5056 6.8417C11.3818 7.05095 11.211 7.23151 10.9932 7.38337C10.7754 7.53524 10.5362 7.61118 10.2756 7.61118H4.93806L3.97564 9.40701H12.0755C12.2314 9.40701 12.3637 9.46413 12.4726 9.57836C12.5815 9.6926 12.6359 9.83043 12.6359 9.99186C12.6359 10.1533 12.5805 10.2888 12.4697 10.3986C12.3589 10.5083 12.2216 10.5631 12.0578 10.5631H4.05035Z'
            fill='white'
          />
        </svg>
      </IconWrap>
    ),
    myPackage: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='46'
          height='46'
          viewBox='0 0 46 46'
          fill='none'
        >
          <circle cx='23' cy='23' r='23' fill='#3EBCBD' />
        </svg>
      </IconWrap>
    ),
    paymentRightBtn: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='80'
          height='80'
          viewBox='0 0 80 80'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <g id='Frame 1761'>
            <circle
              id='Ellipse 272'
              cx='40'
              cy='40'
              r='37.8375'
              stroke='#10CFC9'
              strokeWidth='4.325'
            />
            <path
              id='Vector 259'
              d='M23 38.3514L35.3142 50.6656L57.4799 28.5'
              stroke='#10CFC9'
              strokeWidth='4.325'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </g>
        </svg>
      </IconWrap>
    ),
    user: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='20'
          height='16'
          viewBox='0 0 20 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M7.69925 7.21805C6.6406 7.21805 5.77444 6.8812 5.10075 6.20752C4.42707 5.53383 4.09023 4.66767 4.09023 3.60902C4.09023 2.55038 4.42707 1.68421 5.10075 1.01053C5.77444 0.336842 6.6406 0 7.69925 0C8.75789 0 9.62406 0.336842 10.2977 1.01053C10.9714 1.68421 11.3083 2.55038 11.3083 3.60902C11.3083 4.66767 10.9714 5.53383 10.2977 6.20752C9.62406 6.8812 8.75789 7.21805 7.69925 7.21805ZM0.721804 14.9414C0.513283 14.9414 0.340852 14.8732 0.204511 14.7368C0.0681704 14.6005 0 14.4281 0 14.2195V12.6797C0 12.1183 0.140351 11.613 0.421053 11.1639C0.701754 10.7148 1.10677 10.3699 1.63609 10.1293C2.79098 9.61604 3.86165 9.24712 4.84812 9.02256C5.83459 8.79799 6.78496 8.68571 7.69925 8.68571H8.25263C8.14035 8.91028 8.0401 9.16692 7.95188 9.45564C7.86366 9.74436 7.78747 10.0411 7.72331 10.3459C7.64311 10.7789 7.61504 11.2962 7.6391 11.8977C7.66316 12.4992 7.74737 13.0326 7.89173 13.4977C7.97193 13.7865 8.07218 14.0471 8.19248 14.2797C8.31278 14.5123 8.45313 14.7328 8.61353 14.9414H0.721804ZM14.7729 13.0887C15.3504 13.0887 15.8155 12.9123 16.1684 12.5594C16.5213 12.2065 16.6977 11.7414 16.6977 11.1639C16.6977 10.5865 16.5213 10.1213 16.1684 9.76842C15.8155 9.41554 15.3504 9.2391 14.7729 9.2391C14.1955 9.2391 13.7303 9.41554 13.3774 9.76842C13.0246 10.1213 12.8481 10.5865 12.8481 11.1639C12.8481 11.7414 13.0246 12.2065 13.3774 12.5594C13.7303 12.9123 14.1955 13.0887 14.7729 13.0887ZM13.8827 14.412C13.61 14.3318 13.3333 14.2155 13.0526 14.0632C12.7719 13.9108 12.5353 13.7383 12.3429 13.5459L11.2842 13.7865C11.204 13.8185 11.1238 13.8185 11.0436 13.7865C10.9634 13.7544 10.9073 13.6982 10.8752 13.618L10.5624 13.0647C10.5143 13.0005 10.4982 12.9283 10.5143 12.8481C10.5303 12.7679 10.5704 12.6957 10.6346 12.6316L11.5489 11.7654C11.5168 11.6211 11.5008 11.4206 11.5008 11.1639C11.5008 10.9073 11.5168 10.7068 11.5489 10.5624L10.6346 9.69624C10.5704 9.63208 10.5303 9.5599 10.5143 9.4797C10.4982 9.3995 10.5143 9.32732 10.5624 9.26316L10.8752 8.70977C10.9073 8.62957 10.9634 8.57343 11.0436 8.54135C11.1238 8.50927 11.204 8.50927 11.2842 8.54135L12.3429 8.78195C12.5353 8.58947 12.7719 8.41704 13.0526 8.26466C13.3333 8.11228 13.61 7.99599 13.8827 7.91579L14.0752 6.6406C14.0912 6.54436 14.1313 6.46817 14.1955 6.41203C14.2596 6.35589 14.3398 6.32782 14.4361 6.32782H15.1098C15.206 6.32782 15.2862 6.35589 15.3504 6.41203C15.4145 6.46817 15.4546 6.54436 15.4707 6.6406L15.6632 7.91579C15.9358 7.99599 16.2125 8.11228 16.4932 8.26466C16.7739 8.41704 17.0105 8.58947 17.203 8.78195L18.2617 8.54135C18.3419 8.50927 18.4221 8.50927 18.5023 8.54135C18.5825 8.57343 18.6386 8.62957 18.6707 8.70977L18.9835 9.26316C19.0316 9.32732 19.0476 9.3995 19.0316 9.4797C19.0155 9.5599 18.9754 9.63208 18.9113 9.69624L17.997 10.5624C18.0291 10.7068 18.0451 10.9073 18.0451 11.1639C18.0451 11.4206 18.0291 11.6211 17.997 11.7654L18.9113 12.6316C18.9754 12.6957 19.0155 12.7679 19.0316 12.8481C19.0476 12.9283 19.0316 13.0005 18.9835 13.0647L18.6707 13.618C18.6386 13.6982 18.5825 13.7544 18.5023 13.7865C18.4221 13.8185 18.3419 13.8185 18.2617 13.7865L17.203 13.5459C17.0105 13.7383 16.7739 13.9108 16.4932 14.0632C16.2125 14.2155 15.9358 14.3318 15.6632 14.412L15.4707 15.6872C15.4546 15.7835 15.4145 15.8596 15.3504 15.9158C15.2862 15.9719 15.206 16 15.1098 16H14.4361C14.3398 16 14.2596 15.9719 14.1955 15.9158C14.1313 15.8596 14.0912 15.7835 14.0752 15.6872L13.8827 14.412Z'
            fill='white'
          />
        </svg>
      </IconWrap>
    ),
    editUser: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='15'
          height='12'
          viewBox='0 0 15 12'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M0 11.75V10.0212C0 9.59957 0.105497 9.21802 0.316492 8.87651C0.527475 8.53499 0.82057 8.27829 1.19578 8.10641C2.01676 7.73927 2.75804 7.47401 3.41961 7.31063C4.0812 7.14727 4.76246 7.06559 5.46339 7.06559C6.16944 7.06559 6.84983 7.14659 7.50456 7.30859C8.15929 7.47058 8.89714 7.73652 9.71812 8.10641C10.0926 8.27478 10.3883 8.52982 10.6053 8.87154C10.8222 9.21327 10.9307 9.5964 10.9307 10.0209V11.75H0ZM11.9803 11.75V10.0294C11.9803 9.3011 11.8022 8.69914 11.446 8.22351C11.0898 7.74786 10.6185 7.35154 10.0322 7.03454C10.7818 7.12907 11.4886 7.26388 12.1529 7.43897C12.817 7.61406 13.3588 7.81359 13.778 8.03757C14.1547 8.25556 14.4526 8.53589 14.6715 8.87856C14.8905 9.22123 15 9.60903 15 10.042V11.75H11.9803ZM5.46432 6.01579C4.71019 6.01579 4.09448 5.77074 3.6172 5.28065C3.13991 4.79054 2.90127 4.15795 2.90127 3.3829C2.90127 2.60784 3.13997 1.97526 3.61738 1.48515C4.09479 0.995049 4.71016 0.75 5.4635 0.75C6.21684 0.75 6.83283 0.995095 7.31145 1.48528C7.79008 1.97547 8.02939 2.60732 8.02939 3.38083C8.02939 4.15726 7.79041 4.79054 7.31244 5.28065C6.83449 5.77074 6.21845 6.01579 5.46432 6.01579ZM11.6451 3.3808C11.6451 4.15451 11.4059 4.78711 10.9274 5.27858C10.4489 5.77006 9.83247 6.01579 9.07807 6.01579C8.95234 6.01579 8.81559 6.00651 8.66784 5.98793C8.52008 5.96934 8.38074 5.93573 8.24982 5.8871C8.52332 5.59479 8.72991 5.23602 8.86958 4.81079C9.00924 4.38557 9.07907 3.90981 9.07907 3.3835C9.07907 2.8572 9.00903 2.39205 8.86896 1.98805C8.72887 1.58405 8.52249 1.21408 8.24982 0.87816C8.37539 0.841354 8.51325 0.81079 8.6634 0.786467C8.81354 0.762155 8.95177 0.75 9.07807 0.75C9.83149 0.75 10.4477 0.995766 10.9267 1.4873C11.4056 1.97884 11.6451 2.61001 11.6451 3.3808Z'
            fill='#454E64'
          />
        </svg>
      </IconWrap>
    ),
    edit: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='15'
          height='15'
          viewBox='0 0 15 15'
          fill='none'
        >
          {/* <g clip-path='url(#clip0_4405_28163)'> */}
          <path
            d='M13.6781 4.59771L10.8101 1.6764L11.7512 0.717844C12.0051 0.459186 12.3225 0.333661 12.7034 0.341268C13.0843 0.348876 13.4017 0.482009 13.6557 0.740667L14.6191 1.72205C14.873 1.9807 15 2.30022 15 2.6806C15 3.06098 14.873 3.3805 14.6191 3.63916L13.6781 4.59771ZM12.737 5.55627L2.86793 15.3409H0V12.4196L9.86909 2.63496L12.737 5.55627Z'
            fill='#454E64'
          />
        </svg>
      </IconWrap>
    ),
    delete: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='15'
          height='15'
          viewBox='0 0 15 15'
          fill='none'
        >
          {/* <g clip-path='url(#clip0_4405_28165)'> */}
          <path
            d='M2.36719 15.3409C1.99219 15.3409 1.66406 15.2159 1.38281 14.9659C1.10156 14.7159 0.960938 14.4243 0.960938 14.0909V2.21594H0V0.965942H4.40625V0.340942H10.5938V0.965942H15V2.21594H14.0391V14.0909C14.0391 14.4243 13.8984 14.7159 13.6172 14.9659C13.3359 15.2159 13.0078 15.3409 12.6328 15.3409H2.36719ZM4.85156 12.2993H6.25781V3.98678H4.85156V12.2993ZM8.74219 12.2993H10.1484V3.98678H8.74219V12.2993Z'
            fill='#454E64'
          />
        </svg>
      </IconWrap>
    ),
    addGroup: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='22'
          height='22'
          viewBox='0 0 22 22'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <rect width='22' height='22' rx='11' fill='#454E64' />
          <rect x='6' y='10.2012' width='10' height='1.6' fill='#F8F8F8' />
          <rect x='10.2031' y='6' width='1.6' height='10' fill='#F8F8F8' />
        </svg>
      </IconWrap>
    ),

    questionMark: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='14'
          height='16'
          viewBox='0 0 14 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M7.9 16L7.7 13.8H6.9C4.99333 13.8 3.36667 13.1267 2.02 11.78C0.673333 10.4333 0 8.80667 0 6.9C0 4.99333 0.676667 3.36667 2.03 2.02C3.38333 0.673333 5.02 0 6.94 0C7.88667 0 8.76333 0.17 9.57 0.51C10.3767 0.85 11.08 1.32667 11.68 1.94C12.28 2.55333 12.75 3.27667 13.09 4.11C13.43 4.94333 13.6 5.85333 13.6 6.84C13.6 8.62667 13.0967 10.35 12.09 12.01C11.0833 13.67 9.68667 15 7.9 16ZM6.94 11.54C7.15333 11.54 7.33333 11.4667 7.48 11.32C7.62667 11.1733 7.7 10.9933 7.7 10.78C7.7 10.5667 7.62667 10.3867 7.48 10.24C7.33333 10.0933 7.15333 10.02 6.94 10.02C6.72667 10.02 6.54667 10.0933 6.4 10.24C6.25333 10.3867 6.18 10.5667 6.18 10.78C6.18 10.9933 6.25333 11.1733 6.4 11.32C6.54667 11.4667 6.72667 11.54 6.94 11.54ZM6.9 8.82C7.03333 8.82 7.15 8.77333 7.25 8.68C7.35 8.58667 7.41333 8.46667 7.44 8.32C7.48 8.13333 7.56333 7.95333 7.69 7.78C7.81667 7.60667 8.01333 7.38667 8.28 7.12C8.64 6.76 8.89333 6.43 9.04 6.13C9.18667 5.83 9.26 5.50667 9.26 5.16C9.26 4.56 9.05667 4.06667 8.65 3.68C8.24333 3.29333 7.7 3.1 7.02 3.1C6.55333 3.1 6.12333 3.20333 5.73 3.41C5.33667 3.61667 5.02 3.90667 4.78 4.28C4.7 4.4 4.68667 4.52333 4.74 4.65C4.79333 4.77667 4.89333 4.87333 5.04 4.94C5.14667 4.98 5.26 4.98 5.38 4.94C5.5 4.9 5.6 4.82667 5.68 4.72C5.82667 4.50667 6.01 4.35 6.23 4.25C6.45 4.15 6.69333 4.1 6.96 4.1C7.36 4.1 7.67333 4.19667 7.9 4.39C8.12667 4.58333 8.24 4.84 8.24 5.16C8.24 5.41333 8.16667 5.67 8.02 5.93C7.87333 6.19 7.61333 6.51333 7.24 6.9C6.96 7.19333 6.76 7.43667 6.64 7.63C6.52 7.82333 6.44667 8.05333 6.42 8.32C6.40667 8.45333 6.44667 8.57 6.54 8.67C6.63333 8.77 6.75333 8.82 6.9 8.82Z'
            fill='white'
          />
        </svg>
      </IconWrap>
    ),
    addSubgroups: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='54'
          height='36'
          viewBox='0 0 54 36'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M0 5C0 2.23858 2.23858 0 5 0H49C51.7614 0 54 2.23858 54 5V36H0V5Z'
            fill='#3EBCBD'
          />
          <rect
            x='21.6328'
            y='18.2188'
            width='10.5469'
            height='1.37568'
            fill='white'
          />
          <rect
            x='27.5938'
            y='13.6328'
            width='10.5469'
            height='1.37568'
            transform='rotate(90 27.5938 13.6328)'
            fill='white'
          />
        </svg>
      </IconWrap>
    ),

    product: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='19'
          height='16'
          viewBox='0 0 19 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M3.33809 16C3.11441 16 2.9152 15.9336 2.74045 15.8008C2.5657 15.668 2.45037 15.4967 2.39445 15.287L0.0248532 6.75229C-0.0310664 6.5426 0.00737828 6.35387 0.140187 6.18611C0.272996 6.01835 0.45124 5.93447 0.674919 5.93447H4.86889L8.74831 0.272608C8.81821 0.188729 8.8951 0.122324 8.97898 0.0733945C9.06286 0.0244648 9.16072 0 9.27256 0C9.3844 0 9.48226 0.0244648 9.56614 0.0733945C9.65002 0.122324 9.7269 0.188729 9.7968 0.272608L13.6553 5.93447H17.9541C18.1638 5.93447 18.335 6.01835 18.4678 6.18611C18.6006 6.35387 18.6391 6.5426 18.5832 6.75229L16.1926 15.287C16.1367 15.4967 16.0214 15.668 15.8466 15.8008C15.6719 15.9336 15.4726 16 15.249 16H3.33809ZM9.29353 12.2254C9.64303 12.2254 9.9401 12.1031 10.1847 11.8585C10.4294 11.6138 10.5517 11.3167 10.5517 10.9672C10.5517 10.6177 10.4294 10.3207 10.1847 10.076C9.9401 9.83137 9.64303 9.70904 9.29353 9.70904C8.94403 9.70904 8.64696 9.83137 8.40231 10.076C8.15766 10.3207 8.03534 10.6177 8.03534 10.9672C8.03534 11.3167 8.15766 11.6138 8.40231 11.8585C8.64696 12.1031 8.94403 12.2254 9.29353 12.2254ZM6.39969 5.93447H12.1245L9.27256 1.7405L6.39969 5.93447Z'
            fill='#C5C5C5'
          />
        </svg>
      </IconWrap>
    ),
    calendarStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='13'
          height='13'
          viewBox='0 0 13 13'
          fill='none'
        >
          <rect
            x='0.4'
            y='1.4'
            width='12.2'
            height='11.2'
            rx='1.6'
            stroke='#444B52'
            strokeWidth='0.8'
            style={{fill: 'none'}}
          />
          <path
            d='M0 3C0 1.89543 0.895431 1 2 1H11C12.1046 1 13 1.89543 13 3V4.6H0V3Z'
            fill='#444B52'
            style={{stroke: 'none'}}
          />
          <rect
            x='2.80005'
            y='6.2998'
            width='4.4'
            height='0.8'
            rx='0.4'
            fill='#444B52'
            style={{stroke: 'none'}}
          />
          <rect
            x='2.80005'
            y='8'
            width='3.2'
            height='0.8'
            rx='0.4'
            fill='#444B52'
            style={{stroke: 'none'}}
          />
          <rect
            x='3.80005'
            width='3.2'
            height='0.8'
            rx='0.4'
            transform='rotate(90 3.80005 0)'
            fill='#444B52'
            style={{stroke: 'none'}}
          />
          <rect
            x='10'
            width='3.2'
            height='0.8'
            rx='0.4'
            transform='rotate(90 10 0)'
            fill='#444B52'
            style={{stroke: 'none'}}
          />
          <rect
            x='2.80005'
            y='9.7002'
            width='5.5'
            height='0.8'
            rx='0.4'
            fill='#444B52'
            style={{stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    checkFill: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='12'
          height='10'
          viewBox='0 0 12 10'
          fill='none'
        >
          <rect
            x='0.399902'
            y='4.76611'
            width='2.00912'
            height='6.2547'
            rx='1.00456'
            transform='rotate(-45 0.399902 4.76611)'
            style={{stroke: 'none'}}
          />
          <path
            d='M9.32489 1.11205C9.70587 0.673046 10.3792 0.649252 10.7903 1.06027C11.1622 1.43219 11.1832 2.02837 10.8385 2.42561L5.58899 8.47457C5.20801 8.91358 4.53465 8.93737 4.12363 8.52635C3.75171 8.15443 3.73064 7.55825 4.07539 7.16101L9.32489 1.11205Z'
            style={{stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    idStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='11'
          height='12'
          viewBox='0 0 11 12'
          fill='none'
        >
          <circle cx='5.5' cy='2.5' r='2' style={{fill: 'none'}} />
          <path
            d='M1.51923 7.46414C2.90385 6.02368 4.92308 5.93637 5.58654 6.02367C7.57692 6.02367 8.52885 6.67842 9.30769 7.46414C9.74039 7.90065 9.91346 8.23073 10 8.77372V10.3452C10 11 9.48077 11 9.48077 11L9.04808 11L6.45192 11H1.51923C1.17308 11 1.02885 10.5635 1 10.3452V8.77372C1 8.2499 1.34615 7.68241 1.51923 7.46414Z'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    pwStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='11'
          height='13'
          viewBox='0 0 11 13'
          fill='none'
        >
          <rect
            x='0.5'
            y='4.5'
            width='10'
            height='8'
            rx='1.5'
            fill='white'
            style={{fill: 'none'}}
          />
          <path
            d='M3 4.00002V2.71912C3 2.00002 3.5 0.5 5.2 0.5C7 0.5 7.5 1.5 7.7 2.23'
            strokeLinecap='round'
            style={{fill: 'none'}}
          />
          <circle
            r='1'
            transform='matrix(1 0 0 -1 5.3999 8)'
            style={{stroke: 'none'}}
          />
          <path
            d='M5.3501 7.3501L5.3501 9.6501'
            strokeWidth='0.7'
            strokeLinecap='round'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    analysisFill: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <rect
            x='9.91309'
            y='11.9785'
            width='2.7561'
            height='5.30019'
            rx='1.37805'
            transform='rotate(-41.9727 9.91309 11.9785)'
            fill='#10CFC9'
            style={{stroke: 'none'}}
          />
          <circle
            cx='8.32314'
            cy='7.91054'
            r='4.80019'
            stroke={Colors.common.secondary}
            style={{fill: 'none', stroke: Colors.common.secondary}}
          />
          <path
            d='M12.5933 7.38075C12.5933 10.0318 10.4442 12.1809 7.79311 12.1809C5.14204 12.1809 2.99292 10.0318 2.99292 7.38075C2.99292 4.72968 5.14204 2.58057 7.79311 2.58057C10.4442 2.58057 12.5933 4.72968 12.5933 7.38075Z'
            fill='#10CFC9'
            stroke='#10CFC9'
          />
          <path
            d='M4.61304 8.44083L6.10956 7.02745L7.10724 8.44083L9.10261 6.08519L10.1003 7.49858L13.0933 4.20068'
            stroke={Colors.common.secondary}
            strokeWidth='0.8'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none', stroke: Colors.common.secondary}}
          />
        </svg>
      </IconWrap>
    ),
    analysisStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M4.12012 8.36027L5.61664 6.94688L6.61432 8.36027L8.60969 6.00463L9.60737 7.41801L12.6004 4.12012'
            stroke='white'
            strokeWidth='0.8'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
          <rect
            x='10.1265'
            y='11.9353'
            width='1.7561'
            height='4.30019'
            rx='0.878049'
            transform='rotate(-41.9727 10.1265 11.9353)'
            fill='white'
            stroke='white'
          />
          <circle
            cx='7.83046'
            cy='7.83046'
            r='4.80019'
            stroke={Colors.common.secondary}
            style={{fill: 'none', stroke: Colors.common.secondary}}
          />
          <path
            d='M12.1004 7.30019C12.1004 9.95126 9.95126 12.1004 7.30019 12.1004C4.64912 12.1004 2.5 9.95126 2.5 7.30019C2.5 4.64912 4.64912 2.5 7.30019 2.5C9.95126 2.5 12.1004 4.64912 12.1004 7.30019Z'
            stroke='white'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    communityStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <mask
            id='mask0_1101_1377'
            style={{maskType: 'alpha'}}
            maskUnits='userSpaceOnUse'
            x='2'
            y='3'
            width='14'
            height='13'
          >
            <path
              d='M9.0106 5.28589C9.0106 5.28589 10.0422 3.80334 11.4299 3.68818C11.4299 3.68818 13.1215 3.38529 14.3386 4.73737C14.3386 4.73737 15.8584 5.99609 15.1909 8.60363C15.1909 8.60363 15.2117 9.44364 12.7775 11.867L9.14862 15.4011L4.71672 11.2043C4.71672 11.2043 2.39608 9.239 2.66096 7.08953C2.66096 7.08953 2.7989 4.45256 5.284 3.74966C5.28276 3.74901 7.25106 3.18316 9.0106 5.28589Z'
              fill='white'
              stroke='#231815'
              strokeLinecap='round'
              strokeLinejoin='round'
              style={{stroke: '#231815'}}
            />
          </mask>
          <g mask='url(#mask0_1101_1377)'>
            <rect
              x='4.75'
              y='9.30127'
              width='2.22037'
              height='4.15259'
              rx='1.11018'
              stroke='white'
              style={{fill: 'none'}}
            />
            <path
              d='M11.1907 10.325C11.1907 9.71189 11.6877 9.21484 12.3009 9.21484C12.914 9.21484 13.411 9.71189 13.411 10.325V12.2572C13.411 12.8704 12.914 13.3674 12.3009 13.3674C11.6877 13.3674 11.1907 12.8704 11.1907 12.2573V10.325Z'
              stroke='white'
              style={{fill: 'none'}}
            />
            <path
              d='M7.32642 10.7338C7.32642 9.7649 8.11182 8.97949 9.08067 8.97949C10.0495 8.97949 10.8349 9.7649 10.8349 10.7338V13.31C10.8349 14.2789 10.0495 15.0643 9.08067 15.0643C8.11182 15.0643 7.32642 14.2789 7.32642 13.31V10.7338Z'
              stroke='white'
              style={{fill: 'none'}}
            />
            <path
              d='M13.0533 8.22557C13.0533 8.57268 12.7719 8.85406 12.4248 8.85406C12.0777 8.85406 11.7963 8.57268 11.7963 8.22557C11.7963 7.87846 12.0777 7.59707 12.4248 7.59707C12.7719 7.59707 13.0533 7.87846 13.0533 8.22557Z'
              stroke='white'
              strokeWidth='0.7'
              style={{fill: 'none'}}
            />
            <path
              d='M6.20367 8.22557C6.20367 8.57268 5.92228 8.85406 5.57518 8.85406C5.22807 8.85406 4.94668 8.57268 4.94668 8.22557C4.94668 7.87846 5.22807 7.59707 5.57518 7.59707C5.92228 7.59707 6.20367 7.87846 6.20367 8.22557Z'
              stroke='white'
              strokeWidth='0.7'
              style={{fill: 'none'}}
            />
            <path
              d='M9.96772 7.24704C9.96772 7.78151 9.53444 8.21479 8.99997 8.21479C8.4655 8.21479 8.03223 7.78151 8.03223 7.24704C8.03223 6.71257 8.4655 6.2793 8.99997 6.2793C9.53444 6.2793 9.96772 6.71257 9.96772 7.24704Z'
              stroke='white'
              style={{fill: 'none'}}
            />
          </g>
          <path
            d='M9.06792 4.40791C9.06792 4.40791 10.093 2.85744 11.7519 2.65315C11.7519 2.65315 13.8256 2.24455 15.2771 3.67463C15.2771 3.67463 17.1436 5.10471 16.1068 8.16917C16.1068 8.16917 15.4845 10.2121 13.2436 11.9158L9.22092 15.3196L6.56782 13.0723L4.07943 11.0293C4.07943 11.0293 1.38368 8.98635 1.59123 6.34751C1.59123 6.34751 1.79841 3.26603 4.93698 2.75074C4.93698 2.75074 7.39727 2.24455 9.06792 4.40791Z'
            stroke='white'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    displayDescription: (
      <svg
        width='19'
        height='19'
        viewBox='0 0 19 19'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M2.5 15.274L5.5438 10.2734L6.73958 11.6866L9.02243 12.9911L6.30475 17.6655L5.5438 14.6217L2.5 15.274Z'
          fill='#353E56'
          stroke='white'
        />
        <path
          d='M15.9795 15.274L12.9357 10.2734L11.7399 11.6866L9.45706 12.9911L12.1747 17.6655L12.9357 14.6217L15.9795 15.274Z'
          fill='#353E56'
          stroke='white'
        />
        <circle
          cx='9.34865'
          cy='7.01271'
          r='5.69631'
          fill='#353E56'
          stroke='white'
        />
        <circle
          cx='9.34961'
          cy='6.99609'
          r='4.2'
          stroke='white'
          strokeWidth='0.6'
        />
        <path
          d='M9.74481 4.64873L9.74481 4.64874L10.3 5.77331L11.4973 5.97613L11.4973 5.97614L11.494 5.99586C11.5709 6.0087 11.6429 6.04348 11.7021 6.09636C11.7612 6.14924 11.8052 6.21815 11.8291 6.29546L9.74481 4.64873ZM9.74481 4.64873C9.70757 4.57335 9.65107 4.51 9.58142 4.46572C9.51177 4.42144 9.43168 4.39797 9.34995 4.39797C9.26822 4.39797 9.18813 4.42144 9.11847 4.46572C9.04882 4.51 8.99233 4.57335 8.95509 4.64873L8.95509 4.64874M9.74481 4.64873L8.95509 4.64874M8.95509 4.64874L8.3999 5.77331L7.20261 5.97614L7.2026 5.97614M8.95509 4.64874L7.2026 5.97614M7.2026 5.97614C7.12195 5.98982 7.04657 6.02638 6.9847 6.0817C6.92284 6.13702 6.87688 6.20894 6.85176 6.28953C6.82664 6.37011 6.8233 6.45632 6.8421 6.53873C6.8609 6.62115 6.90114 6.69667 6.9585 6.75697L6.9585 6.75698M7.2026 5.97614L6.9585 6.75698M6.9585 6.75698L7.81324 7.65513M6.9585 6.75698L7.81324 7.65513M10.4892 7.52144L10.4959 7.51438L11.4758 6.48473L10.4892 7.52144ZM10.4892 7.52144L10.4906 7.53109M10.4892 7.52144L10.4906 7.53109M10.4906 7.53109L10.7027 8.96447L10.7027 8.96457M10.4906 7.53109L10.7027 8.96457M10.7027 8.96457C10.7048 8.97824 10.7032 8.99224 10.6982 9.00498C10.6931 9.01773 10.6848 9.02865 10.6743 9.03661C10.6638 9.04456 10.6515 9.04927 10.6387 9.05031C10.6259 9.05135 10.6131 9.04869 10.6016 9.04257L10.6015 9.04252M10.7027 8.96457L10.6015 9.04252M10.6015 9.04252L9.35926 8.38927L9.34995 8.38438M10.6015 9.04252L9.34995 8.38438M9.34995 8.38438L9.34064 8.38927M9.34995 8.38438L9.34064 8.38927M9.34064 8.38927L8.09839 9.04254L8.09829 9.04259M9.34064 8.38927L8.09829 9.04259M8.09829 9.04259C8.08679 9.04872 8.07396 9.05138 8.06118 9.05034C8.04841 9.0493 8.03608 9.04459 8.02557 9.03663C8.01506 9.02867 8.00677 9.01775 8.00171 9.00501C7.99665 8.99226 7.99507 8.97826 7.99717 8.96459L7.99718 8.96449M8.09829 9.04259L7.99718 8.96449M7.99718 8.96449L8.20932 7.53109L8.21074 7.52144M7.99718 8.96449L8.21074 7.52144M8.21074 7.52144L8.20402 7.51438M8.21074 7.52144L8.20402 7.51438M8.20402 7.51438L7.22415 6.48472M8.20402 7.51438L7.22415 6.48472M8.64617 6.12418L8.6361 6.12589L7.26278 6.35853L8.64617 6.12418ZM8.64617 6.12418L8.65069 6.11502M8.64617 6.12418L8.65069 6.11502M8.65069 6.11502L9.28735 4.82544L9.28735 4.82544M8.65069 6.11502L9.28735 4.82544M9.28735 4.82544C9.29338 4.81322 9.30247 4.80308 9.31351 4.79606C9.32455 4.78904 9.33715 4.78537 9.34995 4.78537C9.36274 4.78537 9.37534 4.78904 9.38638 4.79606C9.39743 4.80308 9.40651 4.81322 9.41254 4.82544L9.41254 4.82544M9.28735 4.82544L9.41254 4.82544M9.41254 4.82544L10.0492 6.11502L10.0537 6.12418M9.41254 4.82544L10.0537 6.12418M10.0537 6.12418L10.0638 6.12589M10.0537 6.12418L10.0638 6.12589M10.0638 6.12589L11.4371 6.35853M10.0638 6.12589L11.4371 6.35853M7.81324 7.65513L7.62821 8.90559L7.81324 7.65513ZM7.22415 6.48472C7.21497 6.47505 7.20844 6.46285 7.20538 6.44941C7.20232 6.43598 7.20287 6.42192 7.20696 6.4088C7.21105 6.39568 7.21849 6.3841 7.22836 6.37527C7.23822 6.36644 7.25013 6.36069 7.26276 6.35853L7.22415 6.48472ZM11.4371 6.35853C11.4498 6.36068 11.4617 6.36644 11.4715 6.37527C11.4814 6.3841 11.4888 6.39568 11.4929 6.4088C11.497 6.42192 11.4976 6.43598 11.4945 6.44941C11.4915 6.46285 11.4849 6.47506 11.4758 6.48471L11.4371 6.35853ZM8.03111 9.43649C8.11261 9.4433 8.1943 9.42654 8.26717 9.38812L7.65688 9.15336C7.68812 9.23166 7.73952 9.29958 7.80553 9.34957C7.87155 9.39957 7.9496 9.42967 8.03111 9.43649Z'
          fill='white'
          stroke='white'
          strokeWidth='0.04'
        />
      </svg>
    ),
    dashboardFill: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <rect
            x='2.535'
            y='2.535'
            width='5.43'
            height='3.93001'
            rx='0.865'
            strokeWidth='0.93'
          />
          <rect
            x='10.035'
            y='11.5345'
            width='5.43'
            height='3.93001'
            rx='0.865'
            strokeWidth='0.93'
          />
          <rect
            x='2.535'
            y='8.53549'
            width='5.43'
            height='6.93001'
            rx='0.865'
            strokeWidth='0.93'
          />
          <rect
            x='10.035'
            y='2.535'
            width='5.43'
            height='6.93001'
            rx='0.865'
            strokeWidth='0.93'
          />
        </svg>
      </IconWrap>
    ),
    dashboardStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <rect
            x='2.535'
            y='2.535'
            width='5.43'
            height='3.93001'
            rx='0.865'
            stroke='white'
            strokeWidth='0.93'
            style={{fill: 'none'}}
          />
          <rect
            x='10.035'
            y='11.5345'
            width='5.43'
            height='3.93001'
            rx='0.865'
            stroke='white'
            strokeWidth='0.93'
            style={{fill: 'none'}}
          />
          <rect
            x='2.535'
            y='8.53549'
            width='5.43'
            height='6.93001'
            rx='0.865'
            stroke='white'
            strokeWidth='0.93'
            style={{fill: 'none'}}
          />
          <rect
            x='10.035'
            y='2.535'
            width='5.43'
            height='6.93001'
            rx='0.865'
            stroke='white'
            strokeWidth='0.93'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    edfFill: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M3.90795 10.8968H6.4776L11.6169 10.8968L13.1109 10.8968C14.0641 10.8968 15.0173 10.5752 15.494 9.62879C16.1644 8.29755 15.9706 6.78947 15.0173 5.84304C13.6921 4.52722 11.9024 5.19936 11.1886 5.50136C11.1886 5.50136 11.4259 3.44569 9.29788 3.47691C7.5831 3.50207 7.1914 4.89737 7.33416 5.50136C7.33416 5.50136 6.10863 4.67892 5.00829 5.36982C3.90795 6.06071 4.33623 7.0076 4.33623 7.31333C3.76519 7.16233 2.43033 7.19209 2.14852 8.68234C1.86671 10.1726 3.19415 10.7458 3.90795 10.8968Z'
            fill='#10CFC9'
            style={{stroke: 'none'}}
          />
          <path
            d='M6.4776 10.8968L6.4776 10.3968H6.4776V10.8968ZM3.90795 10.8968L3.80446 11.386C3.83849 11.3932 3.87317 11.3968 3.90795 11.3968V10.8968ZM2.14852 8.68234L2.63982 8.77525L2.14852 8.68234ZM4.33623 7.31333L4.20841 7.79671C4.35841 7.83638 4.51832 7.80416 4.64126 7.7095C4.7642 7.61485 4.83623 7.46848 4.83623 7.31333H4.33623ZM5.00829 5.36982L5.27417 5.79326L5.00829 5.36982ZM7.33416 5.50136L7.05554 5.91653C7.22632 6.03114 7.44986 6.02948 7.61892 5.91235C7.78798 5.79521 7.86806 5.5865 7.82075 5.38635L7.33416 5.50136ZM9.29788 3.47691L9.30522 3.97686L9.29788 3.47691ZM11.1886 5.50136L10.6919 5.44402C10.6715 5.62073 10.7466 5.79497 10.889 5.90159C11.0313 6.0082 11.2196 6.03115 11.3835 5.96184L11.1886 5.50136ZM15.0173 5.84304L15.3696 5.48822H15.3696L15.0173 5.84304ZM13.1109 10.8968V10.3968H13.1108L13.1109 10.8968ZM13.33 11.3968C13.6062 11.3968 13.83 11.173 13.83 10.8968C13.83 10.6207 13.6062 10.3968 13.33 10.3968V11.3968ZM11.6169 10.8968L11.6169 11.3968H11.6169L11.6169 10.8968ZM15.494 9.62879L15.9405 9.85368L15.494 9.62879ZM6.4776 10.3968H3.90795V11.3968H6.4776V10.3968ZM4.01143 10.4076C3.70218 10.3422 3.28628 10.1883 2.99 9.92073C2.72083 9.67764 2.53437 9.33289 2.63982 8.77525L1.65723 8.58944C1.48087 9.52206 1.81723 10.209 2.31977 10.6629C2.7952 11.0922 3.39992 11.3004 3.80446 11.386L4.01143 10.4076ZM2.63982 8.77525C2.74872 8.19933 3.03645 7.95905 3.31844 7.84707C3.63674 7.72068 4.00005 7.74162 4.20841 7.79671L4.46405 6.82994C4.10136 6.73404 3.51173 6.69436 2.94938 6.91766C2.35074 7.15538 1.83013 7.67511 1.65723 8.58944L2.63982 8.77525ZM4.83623 7.31333C4.83623 7.22838 4.82237 7.14254 4.81296 7.08695C4.79987 7.00961 4.79225 6.97353 4.78014 6.89834C4.75981 6.77212 4.7476 6.64962 4.75786 6.52803C4.77551 6.31878 4.8609 6.05275 5.27417 5.79326L4.74241 4.94637C4.05533 5.37778 3.80469 5.93065 3.7614 6.44399C3.74118 6.6837 3.76727 6.89843 3.79286 7.05733C3.80371 7.1247 3.82095 7.21815 3.82698 7.25381C3.8367 7.31122 3.83623 7.32185 3.83623 7.31333H4.83623ZM5.27417 5.79326C5.66436 5.54827 6.08754 5.5609 6.45414 5.65938C6.6357 5.70815 6.7912 5.7753 6.90115 5.8305C6.95563 5.85785 6.99755 5.88157 7.02438 5.89747C7.03776 5.9054 7.04727 5.91132 7.0526 5.9147C7.05526 5.91638 7.05686 5.91742 7.05737 5.91775C7.05762 5.91792 7.0576 5.9179 7.0573 5.9177C7.05714 5.9176 7.05692 5.91746 7.05663 5.91726C7.05648 5.91716 7.05632 5.91705 7.05614 5.91693C7.05605 5.91687 7.0559 5.91677 7.05585 5.91674C7.0557 5.91664 7.05554 5.91653 7.33416 5.50136C7.61278 5.08618 7.61261 5.08607 7.61244 5.08595C7.61238 5.08591 7.6122 5.08579 7.61207 5.0857C7.61181 5.08553 7.61154 5.08535 7.61124 5.08515C7.61065 5.08476 7.60999 5.08432 7.60924 5.08382C7.60775 5.08284 7.60595 5.08165 7.60386 5.08028C7.59966 5.07754 7.59424 5.07405 7.58766 5.06988C7.57449 5.06154 7.5566 5.05047 7.53432 5.03726C7.48983 5.01089 7.42742 4.97575 7.3498 4.93679C7.19558 4.85937 6.97593 4.76409 6.71357 4.69362C6.19232 4.5536 5.45256 4.50047 4.74241 4.94637L5.27417 5.79326ZM7.82075 5.38635C7.78399 5.23079 7.81155 4.87463 8.03122 4.55569C8.22945 4.26789 8.60188 3.98718 9.30522 3.97686L9.29055 2.97697C8.2791 2.99181 7.59829 3.42133 7.20767 3.98845C6.83849 4.52445 6.74157 5.16793 6.84757 5.61637L7.82075 5.38635ZM9.30522 3.97686C9.75458 3.97027 10.0332 4.07336 10.21 4.19355C10.388 4.31462 10.503 4.4804 10.5779 4.66522C10.6543 4.85367 10.6843 5.05155 10.6933 5.20923C10.6977 5.28647 10.6969 5.34999 10.6951 5.39234C10.6942 5.41341 10.6931 5.42893 10.6924 5.43793C10.6921 5.44242 10.6918 5.44526 10.6917 5.44632C10.6916 5.44685 10.6916 5.44694 10.6917 5.44656C10.6917 5.44637 10.6917 5.44607 10.6918 5.44565C10.6918 5.44543 10.6918 5.44519 10.6918 5.44492C10.6919 5.44479 10.6919 5.44456 10.6919 5.4445C10.6919 5.44426 10.6919 5.44402 11.1886 5.50136C11.6853 5.55869 11.6854 5.55843 11.6854 5.55817C11.6854 5.55807 11.6854 5.5578 11.6855 5.5576C11.6855 5.55721 11.6856 5.55678 11.6856 5.55632C11.6857 5.5554 11.6858 5.55435 11.686 5.55317C11.6862 5.55082 11.6865 5.54796 11.6868 5.5446C11.6875 5.53788 11.6883 5.52918 11.6892 5.51863C11.6909 5.49753 11.6928 5.46891 11.6943 5.43383C11.6972 5.36389 11.6982 5.26705 11.6917 5.15207C11.6787 4.92528 11.635 4.6112 11.5047 4.28963C11.3729 3.96444 11.1481 3.62216 10.7722 3.36657C10.395 3.11011 9.9052 2.96795 9.29055 2.97697L9.30522 3.97686ZM11.3835 5.96184C11.7287 5.81577 12.2896 5.60074 12.903 5.56465C13.5025 5.52939 14.1281 5.66477 14.6651 6.19785L15.3696 5.48822C14.5813 4.70548 13.6494 4.51902 12.8443 4.56638C12.0532 4.61291 11.3624 4.88495 10.9938 5.04087L11.3835 5.96184ZM13.1109 11.3968H13.33V10.3968H13.1109V11.3968ZM13.1108 10.3968L11.6169 10.3968L11.6169 11.3968L13.1109 11.3968L13.1108 10.3968ZM14.665 6.19785C15.4446 6.97186 15.6338 8.23938 15.0474 9.4039L15.9405 9.85368C16.6949 8.35573 16.4965 6.60709 15.3696 5.48822L14.665 6.19785ZM15.0474 9.4039C14.6902 10.1132 13.9666 10.3968 13.1109 10.3968V11.3968C14.1616 11.3968 15.3445 11.0373 15.9405 9.85368L15.0474 9.4039ZM6.4776 11.3968L11.6169 11.3968L11.6169 10.3968L6.4776 10.3968L6.4776 11.3968Z'
            fill='#10CFC9'
            style={{stroke: 'none'}}
          />
          <path
            d='M9.50024 11.4351C9.50024 11.1589 9.27639 10.9351 9.00024 10.9351C8.7241 10.9351 8.50024 11.1589 8.50024 11.4351H9.50024ZM8.50024 13.3284C8.50024 13.6046 8.7241 13.8284 9.00024 13.8284C9.27639 13.8284 9.50024 13.6046 9.50024 13.3284H8.50024ZM8.50024 11.4351V13.3284H9.50024V11.4351H8.50024Z'
            fill='#10CFC9'
            style={{stroke: 'none'}}
          />
          <path
            d='M9.50024 8.25488C9.50024 7.97874 9.27639 7.75488 9.00024 7.75488C8.7241 7.75488 8.50024 7.97874 8.50024 8.25488H9.50024ZM8.50024 10.905V11.405H9.50024V10.905H8.50024ZM8.50024 8.25488V10.905H9.50024V8.25488H8.50024Z'
            fill={Colors.common.secondary}
            style={{stroke: 'none', fill: Colors.common.secondary}}
          />
          <path
            d='M8.96564 14.5232L8.60822 14.8728C8.70097 14.9676 8.82758 15.0217 8.9602 15.0231C9.09282 15.0246 9.22058 14.9733 9.31537 14.8805L8.96564 14.5232ZM7.76757 12.5835C7.57447 12.3861 7.25791 12.3826 7.06051 12.5757C6.86311 12.7688 6.85963 13.0854 7.05274 13.2828L7.76757 12.5835ZM10.94 13.2904C11.1374 13.0973 11.1408 12.7807 10.9476 12.5834C10.7545 12.386 10.4379 12.3826 10.2405 12.5758L10.94 13.2904ZM9.32306 14.1735L7.76757 12.5835L7.05274 13.2828L8.60822 14.8728L9.32306 14.1735ZM9.31537 14.8805L10.94 13.2904L10.2405 12.5758L8.61591 14.1658L9.31537 14.8805Z'
            fill='#10CFC9'
            style={{stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    edfStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M5.75427 11.4C4.66195 11.4 2.9572 11.4 2.9572 11.4C2.18024 11.2291 0.735334 10.5802 1.04208 8.89313C1.34882 7.20611 2.80182 7.17242 3.42339 7.34336C3.42339 6.99727 2.9572 5.92535 4.15492 5.14323C5.35265 4.36111 6.68662 5.29214 6.68662 5.29214C6.53123 4.6084 6.9576 3.02888 8.82413 3.0004C11.1405 2.96505 10.8822 5.29214 10.8822 5.29214C11.6592 4.95027 13.6072 4.18938 15.0497 5.67893C16.0873 6.75034 16.2983 8.45754 15.5685 9.96455C15.0497 11.0359 14.0121 11.4 12.9745 11.4C11.9369 11.4 11.3846 11.4 11.3846 11.4'
            stroke='white'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
          <path
            d='M9 11.3999C9 11.1238 8.77614 10.8999 8.5 10.8999C8.22386 10.8999 8 11.1238 8 11.3999H9ZM8 13.5433C8 13.8194 8.22386 14.0433 8.5 14.0433C8.77614 14.0433 9 13.8194 9 13.5433H8ZM8 11.3999V13.5433H9V11.3999H8Z'
            fill='white'
            style={{stroke: 'none'}}
          />
          <path
            d='M9 9C9 8.72386 8.77614 8.5 8.5 8.5C8.22386 8.5 8 8.72386 8 9H9ZM8 9.6C8 9.87614 8.22386 10.1 8.5 10.1C8.77614 10.1 9 9.87614 9 9.6H8ZM8 9V9.6H9V9H8Z'
            fill='white'
            style={{stroke: 'none'}}
          />
          <path
            d='M8.46242 14.9997L8.09822 15.3423C8.19142 15.4414 8.32097 15.4982 8.45699 15.4997C8.59301 15.5012 8.72376 15.4472 8.81909 15.3501L8.46242 14.9997ZM7.13349 12.8571C6.94429 12.656 6.62785 12.6463 6.42671 12.8355C6.22557 13.0247 6.21589 13.3411 6.40509 13.5423L7.13349 12.8571ZM10.5875 13.5501C10.781 13.3531 10.7782 13.0366 10.5812 12.843C10.3843 12.6495 10.0677 12.6523 9.87416 12.8493L10.5875 13.5501ZM8.82662 14.6571L7.13349 12.8571L6.40509 13.5423L8.09822 15.3423L8.82662 14.6571ZM8.81909 15.3501L10.5875 13.5501L9.87416 12.8493L8.10575 14.6493L8.81909 15.3501Z'
            fill='white'
            style={{stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),

    heartFill: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M9.0106 4.78533C9.0106 4.78533 10.0422 3.31308 11.4299 3.19872C11.4299 3.19872 13.1215 2.89793 14.3386 4.24063C14.3386 4.24063 15.8584 5.4906 15.1909 8.08003C15.1909 8.08003 15.2117 8.91421 12.7775 11.3208L9.14862 14.8303L4.71671 10.6627C4.71671 10.6627 2.39608 8.711 2.66096 6.57645C2.66096 6.57645 2.71244 3.78671 5.284 3.25978C5.284 3.25978 7.41006 2.6399 9.0106 4.78533Z'
            fill='#10CFC9'
            stroke='#10CFC9'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M1.57983 10.2317H6.08908L7.36354 6.98584L8.99731 11.226L10.5659 7.8922L11.3828 10.2021H16.4204'
            stroke='#10CFC9'
            strokeWidth='0.8'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
          <path
            d='M1.57983 9.59595H6.08908L7.36354 6.3501L8.99731 10.5902L10.5659 7.25646L11.3828 9.56634H16.4204'
            stroke={Colors.common.secondary}
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none', stroke: Colors.common.secondary}}
          />
        </svg>
      </IconWrap>
    ),
    heartStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M9.0106 4.78533C9.0106 4.78533 10.0422 3.31308 11.4299 3.19872C11.4299 3.19872 13.1215 2.89793 14.3386 4.24063C14.3386 4.24063 15.8584 5.4906 15.1909 8.08003C15.1909 8.08003 15.2117 8.91421 12.7775 11.3208L9.14862 14.8303L4.71671 10.6627C4.71671 10.6627 2.39608 8.711 2.66096 6.57645C2.66096 6.57645 2.71244 3.78671 5.284 3.25978C5.284 3.25978 7.41006 2.6399 9.0106 4.78533Z'
            stroke='white'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
          <path
            d='M1.57983 10.2317H6.08908L7.36354 6.98584L8.99731 11.226L10.5659 7.8922L11.3828 10.2021H16.4204'
            stroke={Colors.common.secondary}
            strokeWidth='0.8'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
          <path
            d='M1.57983 9.59595H6.08908L7.36354 6.3501L8.99731 10.5902L10.5659 7.25646L11.3828 9.56634H16.4204'
            stroke='white'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    hospitalStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <rect
            x='8.46997'
            y='8.47021'
            width='1.06004'
            height='0.706692'
            fill='white'
            style={{stroke: 'none'}}
          />
          <rect
            x='8.46997'
            y='9.88379'
            width='1.06004'
            height='0.706692'
            fill='white'
            style={{stroke: 'none'}}
          />
          <rect
            x='10.5901'
            y='8.47021'
            width='1.06004'
            height='0.706692'
            fill='white'
            style={{stroke: 'none'}}
          />
          <rect
            x='10.5901'
            y='9.88379'
            width='1.06004'
            height='0.706692'
            fill='white'
            style={{stroke: 'none'}}
          />
          <rect
            x='6.34985'
            y='8.47021'
            width='1.06004'
            height='0.706692'
            fill='white'
            style={{stroke: 'none'}}
          />
          <rect
            x='6.34985'
            y='9.88379'
            width='1.06004'
            height='0.706692'
            fill='white'
            style={{stroke: 'none'}}
          />
          <path
            d='M5.28979 4.06198C5.28979 3.39931 5.76841 3.19116 6.00771 3.16992H11.8707C12.5886 3.16992 12.7282 3.84958 12.7083 4.18941V14.8303H10.3152V12.7914H7.5632V14.8303H5.28979V4.06198Z'
            stroke='white'
            style={{fill: 'none'}}
          />
          <path
            d='M13.7916 5.29004H12.71V14.8304H14.7959C14.8217 12.2909 14.8577 7.08829 14.7959 6.31957C14.7341 5.55085 14.1006 5.31292 13.7916 5.29004Z'
            stroke='white'
            style={{fill: 'none'}}
          />
          <path
            d='M4.20819 5.29004H5.28979V14.8304H3.20385C3.17809 12.2909 3.14204 7.08829 3.20385 6.31957C3.26565 5.55085 3.89916 5.31292 4.20819 5.29004Z'
            stroke='white'
            style={{fill: 'none'}}
          />
          <path
            d='M9.13248 4.49512V5.68766V6.8802M10.325 5.66116H9.13248H7.93994'
            stroke='white'
            strokeWidth='0.8'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    indicationlStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M6.59759 15.5H5.03247C3.22656 15.1486 3.22656 13.7432 3.22656 13.7432V4.37387C3.22656 3.55405 3.78037 2.59369 5.03247 2.5H11.5337C12.0555 2.5 13.2193 2.61712 13.46 4.02252V7.53604M5.51405 5.8964H11.5337M5.51405 8.0045H11.5337M5.51405 10.2297H9.12587M12.7377 10.7928L13.3397 10.2297C13.9416 9.76126 14.3967 9.90881 14.664 9.9955C14.9313 10.0822 15.0917 10.3592 15.1456 10.464C15.3864 10.9324 15.0252 11.518 14.9048 11.7523L14.3028 12.3378M12.7377 10.7928L10.2094 13.1577L9.48705 14.7973C9.40679 14.9535 9.36665 15.2658 9.84823 15.2658L11.7745 14.7973L14.3028 12.3378M12.7377 10.7928L14.3028 12.3378'
            stroke='white'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    myPageStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M8.99732 9.00002C10.5427 9.00002 11.7954 7.74726 11.7954 6.20191C11.7954 4.65656 10.5427 3.40381 8.99732 3.40381C7.45197 3.40381 6.19922 4.65656 6.19922 6.20191C6.19922 7.74726 7.45197 9.00002 8.99732 9.00002Z'
            stroke='white'
            strokeMiterlimit='10'
            style={{fill: 'none'}}
          />
          <path
            d='M5.47798 9C5.47798 9 4.65301 9.93526 4.31717 10.7354C4.31717 10.7354 3.96961 11.6606 3.96582 12.2405C3.96582 12.2405 3.96077 12.9173 4.31715 13.2238C4.31715 13.2238 4.83661 13.8838 6.6388 14.314C6.6388 14.314 9.44887 15.0664 12.2286 14.1141C12.2286 14.1141 13.1297 13.7456 13.4807 13.5C13.4807 13.5 14.0487 13.2159 14.0319 12.3479C14.0319 12.3479 14.1541 10.8889 12.5352 9'
            stroke='white'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    mySetStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <circle cx='7.625' cy='4.625' r='2.5' stroke='white' />
          <path
            d='M3.20192 10.0891C4.74038 8.64868 6.98397 8.56137 7.72115 8.64867C9.93269 8.64867 10.9904 9.30342 11.8558 10.0891C12.3365 10.5257 12.5288 10.8557 12.625 11.3987V12.9702C12.625 13.625 12.0481 13.625 12.0481 13.625L11.5673 13.625L8.68269 13.625H3.20192C2.81731 13.625 2.65705 13.1885 2.625 12.9702V11.3987C2.625 10.8749 3.00962 10.3074 3.20192 10.0891Z'
            stroke='white'
            style={{fill: 'none'}}
          />
          <circle cx='12.25' cy='13' r='1' fill={Colors.common.secondary} />
          <path
            d='M11.2607 10.3393V10.7899C11.1372 10.8356 11.0184 10.8908 10.905 10.9547L10.607 10.6567C10.2695 10.3192 9.72227 10.3192 9.38474 10.6567C9.04722 10.9942 9.04722 11.5415 9.38474 11.879L9.76646 12.2607H9.33929C8.86195 12.2607 8.475 12.6477 8.475 13.125C8.475 13.6023 8.86195 13.9893 9.33929 13.9893H9.78985C9.81707 14.0628 9.84767 14.1347 9.88145 14.2048L9.47646 14.5485C9.11253 14.8574 9.06789 15.4028 9.37676 15.7667C9.68563 16.1306 10.231 16.1753 10.595 15.8664L11.1338 15.4091C11.1755 15.4272 11.2178 15.4443 11.2607 15.4601V15.9107C11.2607 16.388 11.6477 16.775 12.125 16.775C12.6023 16.775 12.9893 16.388 12.9893 15.9107V15.4835L13.3243 15.8186C13.6619 16.1561 14.2091 16.1561 14.5466 15.8186C14.8841 15.4811 14.8841 14.9338 14.5466 14.5963L14.2953 14.345C14.3592 14.2316 14.4144 14.1128 14.4601 13.9893H14.9107C15.388 13.9893 15.775 13.6023 15.775 13.125C15.775 12.6477 15.388 12.2607 14.9107 12.2607H14.4601C14.4424 12.2129 14.4233 12.1657 14.4027 12.1193L14.767 11.8162C15.134 11.5109 15.184 10.966 14.8787 10.599C14.5734 10.2321 14.0285 10.1821 13.6615 10.4874L13.194 10.8763C13.1274 10.8446 13.0591 10.8157 12.9893 10.7899V10.3393C12.9893 9.86195 12.6023 9.475 12.125 9.475C11.6477 9.475 11.2607 9.86195 11.2607 10.3393ZM12.8857 13.125C12.8857 13.5451 12.5451 13.8857 12.125 13.8857C11.7049 13.8857 11.3643 13.5451 11.3643 13.125C11.3643 12.7049 11.7049 12.3643 12.125 12.3643C12.5451 12.3643 12.8857 12.7049 12.8857 13.125Z'
            fill='white'
            stroke={Colors.common.secondary}
            strokeWidth='0.8'
            strokeLinecap='round'
            style={{stroke: Colors.common.secondary}}
          />
        </svg>
      </IconWrap>
    ),
    salesStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <mask
            id='path-1-inside-1_1101_1381'
            fill='white'
            style={{stroke: 'none'}}
          >
            <rect
              x='3.08081'
              y='9.65967'
              width='3.24247'
              height='4.8637'
              rx='0.5'
              style={{stroke: 'none'}}
            />
          </mask>
          <rect
            x='3.08081'
            y='9.65967'
            width='3.24247'
            height='4.8637'
            rx='0.5'
            stroke='white'
            strokeWidth='2'
            mask='url(#path-1-inside-1_1101_1381)'
            style={{fill: 'none'}}
          />
          <path
            d='M7.80029 8.14355L9.98041 8.14355L9.98041 14.0234L7.80029 14.0234L7.80029 8.14355Z'
            stroke='white'
            style={{fill: 'none'}}
          />
          <mask id='path-3-inside-2_1101_1381' fill='white'>
            <rect
              x='11.4573'
              y='5.60645'
              width='3.24247'
              height='8.91679'
              rx='0.5'
              style={{stroke: 'none'}}
            />
          </mask>
          <rect
            x='11.4573'
            y='5.60645'
            width='3.24247'
            height='8.91679'
            rx='0.5'
            stroke='white'
            strokeWidth='2'
            mask='url(#path-3-inside-2_1101_1381)'
            style={{fill: 'none'}}
          />
          <line
            x1='2.45'
            y1='14.0734'
            x2='15.3305'
            y2='14.0734'
            stroke='white'
            strokeWidth='0.9'
            strokeLinecap='round'
            style={{fill: 'none'}}
          />
          <path
            d='M5.16265 7.12352C5.16265 7.51566 4.84476 7.83356 4.45262 7.83356C4.06047 7.83356 3.74258 7.51566 3.74258 7.12352C3.74258 6.73138 4.06047 6.41348 4.45262 6.41348C4.84476 6.41348 5.16265 6.73138 5.16265 7.12352ZM9.48594 5.00342C9.48594 5.39556 9.16805 5.71345 8.77591 5.71345C8.38376 5.71345 8.06587 5.39556 8.06587 5.00342C8.06587 4.61127 8.38376 4.29338 8.77591 4.29338C9.16805 4.29338 9.48594 4.61127 9.48594 5.00342ZM13.7677 3.06004C13.7677 3.45218 13.4498 3.77007 13.0576 3.77007C12.6655 3.77007 12.3476 3.45218 12.3476 3.06004C12.3476 2.66789 12.6655 2.35 13.0576 2.35C13.4498 2.35 13.7677 2.66789 13.7677 3.06004Z'
            fill='#353E55'
            stroke='white'
            strokeWidth='0.7'
            style={{fill: 'none'}}
          />
          <g filter='url(#filter0_d_1101_1381)'>
            <path
              d='M5.37769 6.68681L7.80954 5.47089M9.70098 4.52517L12.1328 3.44434'
              stroke='white'
              strokeWidth='0.7'
              strokeLinecap='square'
              style={{fill: 'none'}}
            />
          </g>
          <path
            d='M2.54053 7.0127C2.34723 7.0127 2.19053 7.1694 2.19053 7.3627C2.19053 7.556 2.34723 7.7127 2.54053 7.7127V7.0127ZM3.43221 7.7127H3.78221V7.0127H3.43221V7.7127ZM14.97 3.2543C15.1633 3.2543 15.32 3.0976 15.32 2.9043C15.32 2.711 15.1633 2.5543 14.97 2.5543V3.2543ZM14.1594 2.5543H13.8094V3.2543H14.1594V2.5543ZM2.54053 7.7127H3.43221V7.0127H2.54053V7.7127ZM14.97 2.5543H14.1594V3.2543H14.97V2.5543Z'
            fill='white'
            style={{stroke: 'none'}}
          />
          <defs>
            <filter
              id='filter0_d_1101_1381'
              x='0.908203'
              y='2.98242'
              width='15.6865'
              height='12.1738'
              filterUnits='userSpaceOnUse'
              colorInterpolationFilters='sRGB'
            >
              <feFlood floodOpacity='0' result='BackgroundImageFix' />
              <feColorMatrix
                in='SourceAlpha'
                type='matrix'
                values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                result='hardAlpha'
              />
              <feOffset dy='4' />
              <feGaussianBlur stdDeviation='2' />
              <feComposite in2='hardAlpha' operator='out' />
              <feColorMatrix
                type='matrix'
                values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0'
              />
              <feBlend
                mode='normal'
                in2='BackgroundImageFix'
                result='effect1_dropShadow_1101_1381'
              />
              <feBlend
                mode='normal'
                in='SourceGraphic'
                in2='effect1_dropShadow_1101_1381'
                result='shape'
              />
            </filter>
          </defs>
        </svg>
      </IconWrap>
    ),
    salesMenuStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='19'
          height='20'
          viewBox='0 0 19 20'
          style={{fill: 'none'}}
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M2.5 15.358L5.5438 10.3574L6.73958 11.7706L9.02243 13.0751L6.30475 17.7495L5.5438 14.7057L2.5 15.358Z'
            style={{fill: '#353E56', stroke: 'white'}}
          />
          <path
            d='M15.9795 15.358L12.9357 10.3574L11.7399 11.7706L9.45706 13.0751L12.1747 17.7495L12.9357 14.7057L15.9795 15.358Z'
            style={{fill: '#353E56', stroke: 'white'}}
          />
          <circle
            cx='9.34865'
            cy='7.0967'
            r='5.69631'
            style={{fill: '#353E56', stroke: 'white'}}
          />
          <circle
            cx='9.34961'
            cy='7.08008'
            r='4.2'
            style={{fill: '#353E56', stroke: 'white'}}
            strokeWidth='0.6'
          />
          <path
            d='M9.74432 4.73076L9.74432 4.73077L10.2995 5.85534L11.4968 6.05816L11.4968 6.05817L11.4935 6.07789C11.5704 6.09073 11.6425 6.12551 11.7016 6.17839C11.7608 6.23127 11.8047 6.30018 11.8286 6.37749L9.74432 4.73076ZM9.74432 4.73076C9.70708 4.65538 9.65059 4.59203 9.58094 4.54775C9.51128 4.50347 9.43119 4.48 9.34946 4.48C9.26773 4.48 9.18764 4.50347 9.11798 4.54775C9.04833 4.59203 8.99184 4.65538 8.9546 4.73076L8.9546 4.73077M9.74432 4.73076L8.9546 4.73077M8.9546 4.73077L8.39941 5.85534L7.20212 6.05817L7.20211 6.05817M8.9546 4.73077L7.20211 6.05817M7.20211 6.05817C7.12147 6.07186 7.04608 6.10842 6.98421 6.16374C6.92235 6.21905 6.87639 6.29097 6.85127 6.37156C6.82615 6.45214 6.82281 6.53835 6.84161 6.62076C6.86042 6.70318 6.90065 6.7787 6.95801 6.839L6.95801 6.83901M7.20211 6.05817L6.95801 6.83901M6.95801 6.83901L7.81275 7.73716M6.95801 6.83901L7.81275 7.73716M10.4887 7.60347L10.4954 7.59641L11.4753 6.56676L10.4887 7.60347ZM10.4887 7.60347L10.4901 7.61312M10.4887 7.60347L10.4901 7.61312M10.4901 7.61312L10.7022 9.0465L10.7022 9.0466M10.4901 7.61312L10.7022 9.0466M10.7022 9.0466C10.7043 9.06027 10.7027 9.07427 10.6977 9.08702C10.6926 9.09976 10.6843 9.11068 10.6738 9.11864C10.6633 9.1266 10.651 9.13131 10.6382 9.13234C10.6254 9.13338 10.6126 9.13072 10.6011 9.1246L10.601 9.12455M10.7022 9.0466L10.601 9.12455M10.601 9.12455L9.35877 8.4713L9.34946 8.46641M10.601 9.12455L9.34946 8.46641M9.34946 8.46641L9.34015 8.4713M9.34946 8.46641L9.34015 8.4713M9.34015 8.4713L8.0979 9.12457L8.0978 9.12462M9.34015 8.4713L8.0978 9.12462M8.0978 9.12462C8.0863 9.13075 8.07347 9.13341 8.06069 9.13237C8.04792 9.13133 8.03559 9.12662 8.02509 9.11866C8.01457 9.1107 8.00628 9.09978 8.00122 9.08704C7.99617 9.07429 7.99458 9.06029 7.99668 9.04662L7.99669 9.04652M8.0978 9.12462L7.99669 9.04652M7.99669 9.04652L8.20883 7.61312L8.21026 7.60347M7.99669 9.04652L8.21026 7.60347M8.21026 7.60347L8.20353 7.59641M8.21026 7.60347L8.20353 7.59641M8.20353 7.59641L7.22366 6.56675M8.20353 7.59641L7.22366 6.56675M8.64568 6.20621L8.63561 6.20792L7.26229 6.44056L8.64568 6.20621ZM8.64568 6.20621L8.65021 6.19705M8.64568 6.20621L8.65021 6.19705M8.65021 6.19705L9.28686 4.90747L9.28686 4.90747M8.65021 6.19705L9.28686 4.90747M9.28686 4.90747C9.29289 4.89525 9.30198 4.88512 9.31302 4.87809C9.32406 4.87107 9.33667 4.8674 9.34946 4.8674C9.36225 4.8674 9.37486 4.87107 9.38589 4.87809C9.39694 4.88512 9.40603 4.89525 9.41206 4.90747L9.41206 4.90747M9.28686 4.90747L9.41206 4.90747M9.41206 4.90747L10.0487 6.19705L10.0532 6.20621M9.41206 4.90747L10.0532 6.20621M10.0532 6.20621L10.0633 6.20792M10.0532 6.20621L10.0633 6.20792M10.0633 6.20792L11.4366 6.44056M10.0633 6.20792L11.4366 6.44056M7.81275 7.73716L7.62772 8.98762L7.81275 7.73716ZM7.22366 6.56675C7.21448 6.55708 7.20795 6.54488 7.20489 6.53145C7.20183 6.51801 7.20238 6.50395 7.20647 6.49083C7.21056 6.47772 7.218 6.46613 7.22787 6.4573C7.23773 6.44847 7.24964 6.44272 7.26227 6.44056L7.22366 6.56675ZM11.4366 6.44056C11.4493 6.44271 11.4612 6.44847 11.471 6.4573C11.4809 6.46613 11.4884 6.47772 11.4924 6.49083C11.4965 6.50395 11.4971 6.51801 11.494 6.53145C11.491 6.54488 11.4844 6.55709 11.4753 6.56675L11.4366 6.44056ZM8.03062 9.51852C8.11212 9.52533 8.19381 9.50857 8.26668 9.47015L7.65639 9.23539C7.68763 9.31369 7.73903 9.38161 7.80504 9.4316C7.87106 9.4816 7.94911 9.5117 8.03062 9.51852Z'
            style={{fill: 'white', stroke: 'white'}}
            strokeWidth='0.04'
          />
        </svg>
      </IconWrap>
    ),

    salesManageStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='20'
          height='18'
          viewBox='0 0 20 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M3.98193 4.18023C3.98193 4.18023 3.66489 2.69906 3.46175 1.75H1.45459M3.98193 4.18023L5.35083 10.5756H12.4349C12.7104 10.6182 13.3322 10.4988 13.6156 9.68023C13.899 8.86163 14.2846 6.43992 14.4421 5.4593C14.5242 4.94767 14.2059 4.18023 13.3795 4.18023C12.553 4.18023 6.79959 4.09496 3.98193 4.18023ZM5.23277 10.5756C4.89453 10.6182 4.28822 10.7802 4.28822 11.5988C4.28822 12.4174 4.89453 12.75 5.23277 12.75H8.95191H12.671'
            stroke='white'
            strokeLinecap='round'
            style={{fill: 'none'}}
          />
          <circle
            cx='6.86377'
            cy='14.75'
            r='1'
            stroke='white'
            style={{fill: 'none'}}
          />
          <circle
            cx='10.8638'
            cy='14.75'
            r='1'
            stroke='white'
            style={{fill: 'none'}}
          />
          <path
            d='M14.4311 7.96429V8.41485C14.3076 8.46057 14.1888 8.51581 14.0754 8.57968L13.7774 8.28171C13.4399 7.94419 12.8927 7.94419 12.5552 8.28171C12.2176 8.61924 12.2176 9.16647 12.5552 9.504L12.9369 9.88571H12.5097C12.0324 9.88571 11.6454 10.2727 11.6454 10.75C11.6454 11.2273 12.0324 11.6143 12.5097 11.6143H12.9603C12.9875 11.6878 13.0181 11.7597 13.0519 11.8298L12.6469 12.1735C12.2829 12.4824 12.2383 13.0278 12.5472 13.3917C12.856 13.7556 13.4015 13.8003 13.7654 13.4914L14.3042 13.0341C14.3459 13.0522 14.3882 13.0693 14.4311 13.0851V13.5357C14.4311 14.013 14.8181 14.4 15.2954 14.4C15.7727 14.4 16.1597 14.013 16.1597 13.5357V13.1085L16.4947 13.4436C16.8323 13.7811 17.3795 13.7811 17.717 13.4436C18.0546 13.1061 18.0546 12.5588 17.717 12.2213L17.4657 11.97C17.5296 11.8566 17.5848 11.7378 17.6306 11.6143H18.0811C18.5585 11.6143 18.9454 11.2273 18.9454 10.75C18.9454 10.2727 18.5585 9.88571 18.0811 9.88571H17.6306C17.6128 9.83785 17.5937 9.79068 17.5732 9.74426L17.9375 9.44119C18.3044 9.13591 18.3544 8.59097 18.0491 8.22402C17.7438 7.85707 17.1989 7.80708 16.8319 8.11235L16.3644 8.5013C16.2978 8.46955 16.2295 8.44068 16.1597 8.41485V7.96429C16.1597 7.48695 15.7727 7.1 15.2954 7.1C14.8181 7.1 14.4311 7.48695 14.4311 7.96429ZM16.0561 10.75C16.0561 11.1701 15.7155 11.5107 15.2954 11.5107C14.8753 11.5107 14.5347 11.1701 14.5347 10.75C14.5347 10.3299 14.8753 9.98929 15.2954 9.98929C15.7155 9.98929 16.0561 10.3299 16.0561 10.75Z'
            fill='white'
            stroke={Colors.common.secondary}
            strokeWidth='0.8'
            strokeLinecap='round'
            style={{stroke: Colors.common.secondary}}
          />
        </svg>
      </IconWrap>
    ),
    shopStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M6.84777 7.4673C6.6537 7.96244 6.33565 8.3338 6.20088 8.45759C5.10925 9.40661 3.81547 8.95273 3.12815 8.29254C2.57829 7.76439 2.60255 6.7796 2.68341 6.35323L3.69418 2.63965H6.68604M6.84777 7.4673C7.04183 6.97216 6.92863 4.04256 6.84777 2.63965H6.68604M6.84777 7.4673C6.99601 7.8249 7.46231 8.59788 8.14155 8.82894C8.58628 8.99399 9.65365 9.15079 10.3652 8.45759C10.5135 8.3338 10.8585 7.91293 11.0526 7.21973M11.0526 7.21973C11.0526 7.05468 11.1334 4.09757 11.1738 2.63965H11.0526M11.0526 7.21973C11.1199 7.59109 11.4407 8.40807 12.1846 8.70516C13.1145 9.07652 14.287 9.15904 15.0552 8.00371C15.19 7.86617 15.4352 7.37652 15.3382 6.51827C15.2412 5.66003 14.6913 3.6712 14.4487 2.76343L14.4083 2.63965H11.0526M6.68604 2.63965H11.0526'
            stroke='white'
            style={{fill: 'none'}}
          />
          <path
            d='M3.70434 9V14.542C3.67929 14.7775 3.74943 15.271 4.23038 15.3602H13.7367C13.9246 15.3478 14.3003 15.204 14.3003 14.7279C14.3003 14.2518 14.3003 12.0995 14.3003 11.0829M7.38663 15.323V12.0871C7.38663 11.9383 7.38663 11.6408 7.7248 11.6408C8.10055 11.6408 9.37808 11.6408 10.0544 11.6408C10.2423 11.6408 10.4677 11.678 10.4677 12.0499V15.323'
            stroke='white'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    therapyStroke: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          height='18'
          width='18'
          viewBox='0 0 18 18'
        >
          <path
            d='M8 17v-2H6.5q-.625 0-1.062-.438Q5 14.125 5 13.5V12h-.833q-.688 0-1.021-.604-.334-.604.062-1.188l1.917-2.916q.417-1.896 1.917-3.094T10.5 3q2.292 0 3.896 1.604T16 8.5q0 1.396-.667 2.615-.666 1.218-1.833 1.989V17Zm2.5-8.5Zm-.604 3h1.208l.084-.875q.229-.083.427-.187.197-.105.385-.271l.792.354.604-1.042-.688-.5q.042-.25.042-.489 0-.24-.042-.469l.688-.5-.604-1.042-.792.354q-.167-.166-.375-.27-.208-.105-.437-.188l-.084-.875H9.896l-.084.875q-.229.083-.427.188-.197.104-.385.27l-.792-.354-.604 1.042.688.5q-.042.229-.042.469 0 .239.042.489l-.688.5.604 1.042.792-.354q.167.166.375.271.208.104.437.187Zm.604-2q-.417 0-.708-.292Q9.5 8.917 9.5 8.5t.292-.708q.291-.292.708-.292t.708.292q.292.291.292.708t-.292.708q-.291.292-.708.292Zm-1 6H12v-3.208l.688-.438q.854-.562 1.333-1.448.479-.885.479-1.906 0-1.667-1.167-2.833Q12.167 4.5 10.5 4.5q-1.417 0-2.51.875-1.094.875-1.386 2.25l-.062.271-1.75 2.604H6.5v3h3Z'
            style={{stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    therapyFill: (
      <svg
        width='19'
        height='19'
        viewBox='0 0 19 19'
        style={{fill: 'none'}}
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M11.5755 17.9655V16.8405C11.5755 15.7154 10.6644 15.0404 9.55055 15.0404C7.52541 15.0404 5.95036 14.3654 6.40037 12.3403C6.50883 11.8654 7.12331 10.9903 8.42542 10.9903M6.17528 13.1503C4.15022 12.9253 2.35014 11.1967 3.25028 8.87513C3.66506 7.80534 5.12957 6.91325 6.62528 7.07518M6.85031 7.9751C6.85031 6.74908 7.0753 4.825 9.53805 4.48671C10.4051 4.36761 12.3454 4.64314 12.8662 6.5156C14.1683 6.18121 16.9633 6.51185 16.5133 9.66195C16.5133 9.88491 16.2883 10.562 16.0064 11.1968M16.447 10.5279C17.0981 10.9737 17.9077 12.1307 17.4236 13.5373C16.9633 14.8747 15.6256 15.2654 14.5006 15.0404C13.3756 14.8154 12.7006 13.3435 12.7006 12.3403M13.8428 9.52484C13.9513 10.0821 13.973 11.3304 13.1917 11.8654C12.2152 12.5342 10.9132 11.8656 10.5875 11.1968M8.63436 8.52181C8.85138 8.07594 9.61094 7.31795 10.9131 7.853C11.3471 7.96446 12.1501 8.52174 11.8896 9.85921'
          style={{stroke: 'white', fill: 'none'}}
          strokeWidth='0.892527'
          strokeLinecap='round'
        />
        <circle
          cx='1.67502'
          cy='7.39084'
          r='0.675021'
          style={{fill: 'white', stroke: 'none'}}
        />
        <circle
          cx='5.72531'
          cy='4.24045'
          r='0.675021'
          style={{fill: 'white', stroke: 'none'}}
        />
        <circle
          cx='10.4499'
          cy='2.57541'
          r='0.675021'
          style={{fill: 'white', stroke: 'none'}}
        />
        <circle
          cx='15.1755'
          cy='4.24045'
          r='0.675021'
          style={{fill: 'white', stroke: 'none'}}
        />
        <circle
          cx='18.3249'
          cy='7.39084'
          r='0.675021'
          style={{fill: 'white', stroke: 'none'}}
        />
      </svg>
    ),
    equalFill: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='13'
          height='9'
          viewBox='0 0 13 9'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M0.858461 3.056H12.1445V0.767999H0.858461V3.056ZM0.858461 8.754H12.1445V6.444H0.858461V8.754Z'
            fill='#444B52'
            style={{stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    plusFill: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='12'
          height='12'
          viewBox='0 0 12 12'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path d='M0 5H12V7H0V5Z' fill='#444B52' style={{stroke: 'none'}} />
          <path
            d='M7 8.74229e-08L7 12H5L5 0L7 8.74229e-08Z'
            fill='#444B52'
            style={{stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    minusFill: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='11'
          height='2'
          viewBox='0 0 11 2'
          fill='none'
        >
          <rect width='11' height='2' fill='#444B52' style={{stroke: 'none'}} />
        </svg>
      </IconWrap>
    ),
    personFill: <PersonFillImg />,
    emailFill: <EmailFillImg />,
    searchFill: (
      <svg
        width='14'
        height='14'
        viewBox='0 0 14 14'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M13.77 12.6659L11.1264 10.0301C11.9794 8.94347 12.4422 7.60162 12.4404 6.22022C12.4404 4.98998 12.0756 3.78736 11.3921 2.76445C10.7087 1.74154 9.73719 0.944282 8.60059 0.473489C7.464 0.0026951 6.21332 -0.120486 5.00672 0.119523C3.80011 0.359531 2.69178 0.951949 1.82186 1.82186C0.951949 2.69178 0.359531 3.80011 0.119523 5.00672C-0.120486 6.21332 0.0026951 7.464 0.473489 8.60059C0.944282 9.73719 1.74154 10.7087 2.76445 11.3921C3.78736 12.0756 4.98998 12.4404 6.22022 12.4404C7.60162 12.4422 8.94347 11.9794 10.0301 11.1264L12.6659 13.77C12.7382 13.8429 12.8242 13.9007 12.9189 13.9402C13.0137 13.9797 13.1153 14 13.218 14C13.3206 14 13.4222 13.9797 13.517 13.9402C13.6117 13.9007 13.6977 13.8429 13.77 13.77C13.8429 13.6977 13.9007 13.6117 13.9402 13.517C13.9797 13.4222 14 13.3206 14 13.218C14 13.1153 13.9797 13.0137 13.9402 12.9189C13.9007 12.8242 13.8429 12.7382 13.77 12.6659ZM1.55506 6.22022C1.55506 5.29754 1.82866 4.39558 2.34128 3.62839C2.85389 2.86121 3.58249 2.26327 4.43494 1.91017C5.28739 1.55708 6.2254 1.46469 7.13035 1.6447C8.0353 1.8247 8.86655 2.26902 9.51899 2.92145C10.1714 3.57389 10.6157 4.40514 10.7957 5.31009C10.9757 6.21504 10.8834 7.15305 10.5303 8.0055C10.1772 8.85795 9.57923 9.58655 8.81205 10.0992C8.04486 10.6118 7.1429 10.8854 6.22022 10.8854C4.98294 10.8854 3.79634 10.3939 2.92145 9.51899C2.04656 8.6441 1.55506 7.4575 1.55506 6.22022Z'
          fill={iconColor}
        />
      </svg>
    ),
    starFill: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='14'
          height='14'
          viewBox='0 0 14 14'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M6.61038 12.2348L3.58069 13.8993C3.17145 14.1242 2.66529 13.9597 2.45014 13.5321C2.36447 13.3618 2.3349 13.1667 2.36603 12.9771L2.94465 9.45164C2.99122 9.16785 2.90119 8.87828 2.70388 8.6773L0.252811 6.18055C-0.0782694 5.8433 -0.085044 5.28944 0.23768 4.94345C0.36619 4.80568 0.534577 4.71602 0.716773 4.68835L4.10407 4.174C4.37674 4.13259 4.61245 3.95363 4.73439 3.69543L6.24924 0.487858C6.45386 0.0545928 6.95584 -0.123295 7.37044 0.090534C7.53554 0.175682 7.66917 0.315329 7.75065 0.487858L9.2655 3.69543C9.38744 3.95363 9.62315 4.13259 9.89582 4.174L13.2831 4.68835C13.7407 4.75783 14.0577 5.20176 13.9912 5.6799C13.9647 5.87029 13.8789 6.04626 13.7471 6.18055L11.296 8.6773C11.0987 8.87828 11.0087 9.16785 11.0552 9.45164L11.6339 12.9771C11.712 13.4533 11.406 13.9056 10.9503 13.9872C10.7688 14.0198 10.5822 13.9889 10.4192 13.8993L7.38951 12.2348C7.14562 12.1008 6.85427 12.1008 6.61038 12.2348Z'
            fill='#10CFC9'
          />
        </svg>
      </IconWrap>
    ),
    starStroke: (
      <svg
        width='14'
        height='13'
        viewBox='0 0 14 13'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M13.9412 4.88147C13.8743 4.68046 13.7512 4.5013 13.5856 4.36382C13.42 4.22634 13.2183 4.1359 13.0028 4.10252L9.62216 3.57073L8.05497 0.623014C7.95515 0.435358 7.80385 0.277943 7.61767 0.168042C7.4315 0.0581403 7.21765 0 6.99958 0C6.78151 0 6.56766 0.0581403 6.38149 0.168042C6.19531 0.277943 6.04401 0.435358 5.94418 0.623014L4.377 3.57073L0.996381 4.10252C0.781151 4.13642 0.579729 4.22706 0.414215 4.36448C0.248701 4.50189 0.125491 4.68079 0.0581037 4.88152C-0.00928335 5.08226 -0.0182434 5.29708 0.0322066 5.50242C0.0826566 5.70776 0.190567 5.89569 0.344098 6.04557L2.75618 8.39913L2.2341 11.6755C2.2006 11.8842 2.2272 12.0977 2.31099 12.2927C2.39478 12.4877 2.53251 12.6566 2.70902 12.7807C2.88554 12.9048 3.094 12.9794 3.31149 12.9963C3.52897 13.0132 3.74705 12.9717 3.94175 12.8764L6.99958 11.3832L10.0575 12.8764C10.2522 12.9715 10.4702 13.0129 10.6876 12.9959C10.905 12.9789 11.1134 12.9043 11.2899 12.7802C11.4663 12.6561 11.604 12.4874 11.6879 12.2925C11.7717 12.0976 11.7985 11.8841 11.7651 11.6755L11.2431 8.39913L13.6551 6.04557C13.8092 5.89598 13.9174 5.70805 13.9679 5.50259C14.0184 5.29714 14.0092 5.08216 13.9412 4.88147ZM12.9925 5.40939L10.2487 8.0865L10.8427 11.8133C10.8501 11.8579 10.8445 11.9036 10.8267 11.9454C10.8088 11.9872 10.7794 12.0234 10.7416 12.0499C10.7038 12.0765 10.6592 12.0924 10.6126 12.0959C10.5661 12.0994 10.5194 12.0904 10.4778 12.0698L6.99958 10.3714L3.52128 12.0699C3.4797 12.0905 3.43305 12.0995 3.3865 12.096C3.33994 12.0925 3.2953 12.0765 3.25753 12.05C3.21975 12.0234 3.19031 11.9872 3.17246 11.9455C3.15462 11.9037 3.14907 11.858 3.15643 11.8133L3.75041 8.0865L1.00674 5.40936C0.973971 5.37732 0.950948 5.33717 0.940191 5.2933C0.929434 5.24944 0.931357 5.20356 0.945749 5.16068C0.960141 5.11781 0.986447 5.07959 1.02178 5.05023C1.05712 5.02086 1.10013 5.00147 1.14609 4.99419L4.99146 4.38931L6.77409 1.0364C6.79541 0.996294 6.82773 0.962648 6.86751 0.939157C6.90729 0.915666 6.95298 0.903238 6.99958 0.903238C7.04618 0.903238 7.09187 0.915666 7.13165 0.939157C7.17143 0.962648 7.20375 0.996294 7.22507 1.0364L9.0077 4.38931L12.8531 4.99419C12.899 5.00147 12.942 5.02086 12.9774 5.05023C13.0127 5.07959 13.039 5.11781 13.0534 5.16068C13.0678 5.20356 13.0697 5.24944 13.059 5.2933C13.0482 5.33717 13.0252 5.37735 12.9925 5.40939Z'
          fill={iconColor}
        />
      </svg>
    ),
    prev: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='6'
          height='10'
          viewBox='0 0 6 10'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M4.93763 8.96347L0.947131 5.42817'
            stroke='#EBEBEB'
            strokeWidth='1.3'
            strokeLinecap='round'
          />
          <path
            d='M4.93718 1.49957L0.946777 5.42773'
            stroke='#EBEBEB'
            strokeWidth='1.3'
            strokeLinecap='round'
          />
        </svg>
      </IconWrap>
    ),
    firstPrev: (
      <svg
        width='10'
        height='10'
        viewBox='0 0 10 10'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M5.37966 8.96386L1.38916 5.42856L5.37929 1.5'
          stroke={iconColor}
          strokeWidth='1.3'
          strokeLinecap='round'
        />
        <path
          d='M8.57156 8.9639L4.58105 5.4286L8.57128 1.5'
          stroke={iconColor}
          strokeWidth='1.3'
          strokeLinecap='round'
        />
      </svg>
    ),
    next: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='6'
          height='10'
          viewBox='0 0 6 10'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M1.17516 1.53653L5.16566 5.07183'
            stroke='#444B52'
            strokeWidth='1.3'
            strokeLinecap='round'
          />
          <path
            d='M1.17561 9.00043L5.16602 5.07227'
            stroke='#444B52'
            strokeWidth='1.3'
            strokeLinecap='round'
          />
        </svg>
      </IconWrap>
    ),
    lastNext: (
      <svg
        width='10'
        height='10'
        viewBox='0 0 10 10'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M4.73291 1.53613L8.72341 5.07143L4.73304 8.99964'
          stroke={iconColor}
          strokeWidth='1.3'
          strokeLinecap='round'
        />
        <path
          d='M1.54004 1.53613L5.53054 5.07143L1.54015 8.99964'
          stroke={iconColor}
          strokeWidth='1.3'
          strokeLinecap='round'
        />
      </svg>
    ),
    trashCanFill: (
      <svg
        width='12'
        height='11'
        viewBox='0 0 12 11'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M8.18182 1.83333H11.4545C11.7557 1.83333 12 2.03854 12 2.29167C12 2.5448 11.7557 2.75 11.4545 2.75H10.8518L10.0305 8.96142C9.87698 10.122 8.7096 11 7.3199 11H4.68009C3.29041 11 2.12296 10.122 1.96949 8.96142L1.14815 2.75H0.545455C0.244211 2.75 0 2.5448 0 2.29167C0 2.03854 0.244211 1.83333 0.545455 1.83333H3.81818C3.81818 0.820811 4.79501 0 6 0C7.20499 0 8.18182 0.820811 8.18182 1.83333ZM6 0.916667C5.39751 0.916667 4.90909 1.32708 4.90909 1.83333H7.09091C7.09091 1.32708 6.6025 0.916667 6 0.916667ZM4.36364 4.58333V8.25C4.36364 8.50309 4.60785 8.70833 4.90909 8.70833C5.21033 8.70833 5.45455 8.50309 5.45455 8.25V4.58333C5.45455 4.3302 5.21033 4.125 4.90909 4.125C4.60785 4.125 4.36364 4.3302 4.36364 4.58333ZM7.09091 4.125C6.78967 4.125 6.54545 4.3302 6.54545 4.58333V8.25C6.54545 8.50309 6.78967 8.70833 7.09091 8.70833C7.39215 8.70833 7.63636 8.50309 7.63636 8.25V4.58333C7.63636 4.3302 7.39215 4.125 7.09091 4.125Z'
          fill={iconColor}
        />
      </svg>
    ),
    resetFill: <ResetFillImg />,
    personInfoFill: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='13'
          height='14'
          viewBox='0 0 13 14'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M2.59617 7C2.59617 7 1.72537 7.98722 1.37087 8.83184C1.37087 8.83184 1.004 9.80845 1 10.4205C1 10.4205 0.994664 11.135 1.37084 11.4585C1.37084 11.4585 1.91917 12.1551 3.82148 12.6092C3.82148 12.6092 6.78766 13.4034 9.72185 12.3983C9.72185 12.3983 10.673 12.0092 11.0435 11.75C11.0435 11.75 11.6431 11.4502 11.6253 10.5339C11.6253 10.5339 11.7543 8.99382 10.0455 7'
            stroke='#444B52'
            strokeWidth='1.05556'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          w
          <path
            d='M6.3164 8.77322C5.84383 8.77322 3.75665 7.9856 2.77213 7.5918L2.18142 8.18251L1.59071 8.77322L1 10.5454L1.59071 11.7268L5.13498 12.9082L8.08853 12.3175L11.0421 11.7268L11.6328 10.5454L11.0421 9.95464L10.4514 7.5918H9.26995V8.18251C8.48234 8.37941 6.78897 8.77322 6.3164 8.77322Z'
            fill='#444B52'
          />
          <path
            d='M6.31098 7.00086C7.94218 7.00086 9.26453 5.67851 9.26453 4.0473C9.26453 2.4161 7.94218 1.09375 6.31098 1.09375C4.67977 1.09375 3.35742 2.4161 3.35742 4.0473C3.35742 5.67851 4.67977 7.00086 6.31098 7.00086Z'
            fill='#444B52'
            stroke='#444B52'
            strokeWidth='1.05556'
            strokeMiterlimit='10'
          />
        </svg>
      </IconWrap>
    ),
    hospitalInfoFill: (
      <svg
        width='19'
        height='19'
        viewBox='0 0 19 19'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M14.5577 5.58203H13.416V15.6524H15.6179C15.645 12.9718 15.6831 7.48018 15.6179 6.66876C15.5526 5.85734 14.8839 5.60618 14.5577 5.58203Z'
          fill='#444B52'
          stroke='#444B52'
          strokeWidth='1.05556'
        />
        <path
          d='M4.4418 5.58203H5.5835V15.6524H3.38166C3.35448 12.9718 3.31642 7.48018 3.38166 6.66876C3.4469 5.85734 4.11561 5.60618 4.4418 5.58203Z'
          fill='#444B52'
          stroke='#444B52'
          strokeWidth='1.05556'
        />
        <path
          d='M5.5835 4.28732C5.5835 3.58783 6.0887 3.36812 6.3413 3.3457H12.53C13.2878 3.3457 13.4352 4.06312 13.4141 4.42183V15.6539H10.8881V13.5017H7.9832V15.6539H5.5835V4.28732Z'
          fill='#5A6168'
          stroke='#5A6168'
          strokeWidth='1.05556'
        />
        <rect
          x='8.93945'
          y='8.94141'
          width='1.11893'
          height='0.745952'
          fill='white'
        />
        <rect
          x='8.93945'
          y='10.4336'
          width='1.11893'
          height='0.745952'
          fill='white'
        />
        <rect
          x='11.1787'
          y='8.94141'
          width='1.11893'
          height='0.745952'
          fill='white'
        />
        <rect
          x='11.1787'
          y='10.4355'
          width='1.11893'
          height='0.745952'
          fill='white'
        />
        <rect
          x='6.70166'
          y='8.94141'
          width='1.11893'
          height='0.745952'
          fill='white'
        />
        <rect
          x='6.70166'
          y='10.4336'
          width='1.11893'
          height='0.745952'
          fill='white'
        />
        <path
          d='M9.63965 4.74414V6.00294V7.26173M10.8984 5.97496H9.63965H8.38086'
          stroke='white'
          strokeWidth='0.844444'
        />
      </svg>
    ),
    measurementInfoFill: (
      <svg
        width='19'
        height='19'
        viewBox='0 0 19 19'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <rect
          x='10.4639'
          y='12.6445'
          width='2.90921'
          height='5.59464'
          rx='1.45461'
          transform='rotate(-41.9727 10.4639 12.6445)'
          fill='#444B52'
        />
        <circle
          cx='8.78556'
          cy='8.34855'
          r='5.06686'
          stroke='#F5F5F5'
          strokeWidth='1.05556'
        />
        <path
          d='M13.2929 7.78996C13.2929 10.5883 11.0243 12.8568 8.22599 12.8568C5.42764 12.8568 3.15913 10.5883 3.15913 7.78996C3.15913 4.9916 5.42764 2.72309 8.22599 2.72309C11.0243 2.72309 13.2929 4.9916 13.2929 7.78996Z'
          fill='#444B52'
          stroke='#444B52'
          strokeWidth='1.05556'
        />
        <path
          d='M4.86914 8.90735L6.4488 7.41545L7.50191 8.90735L9.60813 6.42085L10.6612 7.91275L13.8206 4.43164'
          stroke='#F5F5F5'
          strokeWidth='0.844444'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
    OrgInfoFill: (
      <svg
        width='23'
        height='26'
        viewBox='0 0 23 26'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M6.75928 2.0793C6.75928 1.23256 7.37084 0.966592 7.67662 0.939453H15.1682C16.0856 0.939453 16.2639 1.80791 16.2385 2.24213V15.8389H13.1807V13.2335H9.66419V15.8389H6.75928V2.0793Z'
          fill='#444B52'
          stroke='#444B52'
          strokeWidth='1.27778'
        />
        <rect
          x='10.8228'
          y='7.71289'
          width='1.35449'
          height='0.902995'
          fill='white'
        />
        <rect
          x='10.8228'
          y='9.51953'
          width='1.35449'
          height='0.902995'
          fill='white'
        />
        <rect
          x='13.5317'
          y='7.71289'
          width='1.35449'
          height='0.902995'
          fill='white'
        />
        <rect
          x='13.5317'
          y='9.51953'
          width='1.35449'
          height='0.902995'
          fill='white'
        />
        <rect
          x='8.11377'
          y='7.71289'
          width='1.35449'
          height='0.902995'
          fill='white'
        />
        <rect
          x='8.11377'
          y='9.51953'
          width='1.35449'
          height='0.902995'
          fill='white'
        />
        <rect
          x='10.8228'
          y='3.88867'
          width='1.35449'
          height='0.902995'
          fill='white'
        />
        <rect
          x='10.8228'
          y='5.69531'
          width='1.35449'
          height='0.902995'
          fill='white'
        />
        <rect
          x='13.5317'
          y='3.88867'
          width='1.35449'
          height='0.902995'
          fill='white'
        />
        <rect
          x='13.5317'
          y='5.69531'
          width='1.35449'
          height='0.902995'
          fill='white'
        />
        <rect
          x='8.11377'
          y='3.88867'
          width='1.35449'
          height='0.902995'
          fill='white'
        />
        <rect
          x='8.11377'
          y='5.69531'
          width='1.35449'
          height='0.902995'
          fill='white'
        />
        <rect x='4' y='16' width='15' height='2' fill='#444B52' />
      </svg>
    ),
    exclamationMarkFill: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='3'
          height='16'
          viewBox='0 0 3 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <rect y='13' width='2.7' height='2.5' rx='1.25' fill='#EE5454' />
          <rect y='0.5' width='2.7' height='11' rx='1.35' fill='#EE5454' />
        </svg>
      </IconWrap>
    ),
    graphFill: (
      <svg
        width='23'
        height='20'
        viewBox='0 0 23 20'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M8.28799 16.4943H5.24491V13.45H8.28799V16.4943ZM12.8557 8.70358H9.81141V16.4943H12.8557V8.70358ZM17.4221 11.6167H14.3779V16.4943H17.4222V11.6167H17.4221ZM21.9891 6.8703H18.9442V16.4943H21.9891V6.8703ZM1.76147 18.2385V0H0V20H22.9788V18.2383H1.76147V18.2385ZM19.9601 0.799591C19.1176 0.799591 18.4367 1.48052 18.4367 2.31871C18.4367 2.45496 18.4538 2.59104 18.492 2.71437C18.5261 2.84632 18.5771 2.96965 18.6451 3.08024L16.6325 5.79963C16.3772 5.59965 16.0537 5.48045 15.7048 5.48045C15.424 5.48045 15.1601 5.5571 14.9346 5.69318L12.8793 3.25491C12.9389 3.09747 12.9687 2.92298 12.9687 2.74435C12.9687 1.90598 12.2877 1.22524 11.4495 1.22524C10.6071 1.22524 9.92613 1.90616 9.92613 2.74435C9.92613 2.99532 9.98573 3.23373 10.0963 3.44215L7.09632 8.91883C6.98987 8.89764 6.87928 8.88472 6.76869 8.88472C5.9262 8.88472 5.24527 9.56565 5.24527 10.4038C5.24527 10.6379 5.29626 10.8591 5.39427 11.055L2.56429 13.5656L3.12602 14.204L5.96013 11.6933C6.19422 11.8423 6.47069 11.9274 6.76869 11.9274C7.60706 11.9274 8.28781 11.2465 8.28781 10.404C8.28781 9.98698 8.11762 9.60406 7.84098 9.33173L10.7175 4.08053C10.9345 4.19974 11.1855 4.26777 11.4494 4.26777C11.7897 4.26777 12.1089 4.15288 12.3601 3.9572L14.3474 6.3145C14.2409 6.5188 14.1815 6.7529 14.1815 6.99956C14.1815 7.84206 14.8624 8.52298 15.7049 8.52298C16.5433 8.52298 17.2241 7.84206 17.2241 6.99956C17.2241 6.83368 17.1986 6.67606 17.1474 6.52724L19.2623 3.67194C19.4708 3.7784 19.7092 3.84213 19.9601 3.84213C20.7985 3.84213 21.4793 3.1612 21.4793 2.31871C21.4793 1.48052 20.7985 0.799591 19.9601 0.799591Z'
          fill={iconColor}
        />
      </svg>
    ),
    writeEmpty: (
      <svg
        width='15'
        height='15'
        viewBox='0 0 15 15'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        xmlnsXlink='http://www.w3.org/1999/xlink'
      >
        <rect width='15' height='15' fill='url(#pattern0)' />
        <defs>
          <pattern
            id='pattern0'
            patternContentUnits='objectBoundingBox'
            width='1'
            height='1'
          >
            <use
              xlinkHref='#image0_2987_1518'
              transform='scale(0.00195312 0.00195695)'
            />
          </pattern>
          <image
            id='image0_2987_1518'
            width='512'
            height='511'
            xlinkHref='data:image/png;base64,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'
          />
        </defs>
      </svg>
    ),
    radiusPlusFill: (
      <svg
        width='18'
        height='19'
        viewBox='0 0 18 19'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M8.8 5.15C8.6067 5.15 8.45 5.3067 8.45 5.5V8.95H5C4.90717 8.95 4.81815 8.98687 4.75251 9.05251C4.68687 9.11815 4.65 9.20717 4.65 9.3L4.65 9.7C4.65 9.79283 4.68687 9.88185 4.75251 9.94749C4.81815 10.0131 4.90717 10.05 5 10.05H8.45V13.5C8.45 13.6933 8.6067 13.85 8.8 13.85H9.2C9.3933 13.85 9.55 13.6933 9.55 13.5V10.05H13C13.1933 10.05 13.35 9.8933 13.35 9.7V9.3C13.35 9.20717 13.3131 9.11815 13.2475 9.05251C13.1818 8.98687 13.0928 8.95 13 8.95H9.55V5.5C9.55 5.3067 9.3933 5.15 9.2 5.15H8.8Z'
          fill='white'
          stroke='white'
          strokeWidth='0.7'
          strokeLinejoin='round'
        />
      </svg>
    ),
    wirtePenFill: (
      <svg
        width='18'
        height='19'
        viewBox='0 0 18 19'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M2.77996 13.4968L3.3996 11.5421C3.44162 11.3733 3.65087 11.1742 3.68135 11.1586L9.46694 5.87688L11.8534 8.37052L6.07401 13.5203C5.93626 13.646 5.53548 13.7594 5.53548 13.7594L3.80084 14.2064C3.13703 14.3073 2.96794 14.1671 2.88652 14.0309C2.76124 13.8928 2.76748 13.7608 2.77996 13.4968Z'
          fill='white'
        />
        <path
          d='M13.4436 6.88699L12.2902 7.9746L9.84186 5.43047L10.9902 4.32668C11.2806 4.04371 11.3568 3.97889 11.3568 3.97889C11.7198 3.6249 12.8386 3.37948 13.6483 4.24013C14.5106 5.15613 14.1414 6.20414 13.6996 6.63465C13.2578 7.06515 13.5646 6.76908 13.4436 6.88699Z'
          fill='white'
        />
        <rect x='2' y='14.7769' width='13' height='1' rx='0.5' fill='white' />
      </svg>
    ),
    listFill: (
      <svg
        width='18'
        height='19'
        viewBox='0 0 18 19'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M13.5469 5.52539H5.23509C4.98466 5.52539 4.78174 5.72832 4.78174 5.97874V6.39697C4.78174 6.64727 4.98466 6.85032 5.23509 6.85032H13.5468C13.7971 6.85032 14.0001 6.6474 14.0001 6.39697V5.97874C14.0003 5.72845 13.7972 5.52539 13.5469 5.52539Z'
          fill='white'
        />
        <path
          d='M13.5469 8.83789H5.23509C4.98466 8.83789 4.78174 9.04082 4.78174 9.29125V9.70947C4.78174 9.95977 4.98466 10.1628 5.23509 10.1628H13.5468C13.7971 10.1628 14.0001 9.9599 14.0001 9.70947V9.29125C14.0003 9.04082 13.7972 8.83789 13.5469 8.83789Z'
          fill='white'
        />
        <path
          d='M13.5469 12.1504H5.23509C4.98466 12.1504 4.78174 12.3533 4.78174 12.6037V13.022C4.78174 13.2723 4.98466 13.4753 5.23509 13.4753H13.5468C13.7971 13.4753 14.0001 13.2724 14.0001 13.022V12.6037C14.0003 12.3533 13.7972 12.1504 13.5469 12.1504Z'
          fill='white'
        />
        <circle cx='3.5' cy='6.24609' r='0.5' fill='white' />
        <circle cx='3.5' cy='9.64648' r='0.5' fill='white' />
        <circle cx='3.5' cy='12.8457' r='0.5' fill='white' />
      </svg>
    ),
    addPreset: (
      <svg
        width='14'
        height='15'
        viewBox='0 0 14 15'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path fill='#fff' d='M0 6.5h14v2H0z' />
        <path fill='#fff' d='M8 .5v14H6V.5z' />
      </svg>
    ),
    closePreset: (
      <svg
        width='18'
        height='19'
        viewBox='0 0 18 19'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='m4.3 5 9 9m-9 0 9-9'
          stroke={iconColor}
          strokeWidth='1.4'
          strokeLinecap='round'
        />
      </svg>
    ),
  }

  if (svgIcons[name] === undefined) {
    console.log(
      `**${name}**이 **${svgIcons[name]}**입니다. SvgIcon의 props name 확인하고 없으면 추가 필요`,
    )
    svgIcons[name] = null
  }
  return svgIcons[name]
}

export const CommonSvgIcon = (props: SvgIconType) => {
  const {name, iconColor} = props

  /**
   * - IconWrap에 svg 태그를 추가하는 오브젝트
   * - 새로운 아이콘이 필요할 경우 추가해서 사용
   * ***
   * @MUSTDO
   * - svg tag 추가시 fill, stroke가 투명해야하는 rect, circle, d 등의 태그에 inline style로 fill: none, stroke: none 추가
   * - fill, stroke를 css로 제어하면 안되는 경우 inline style로 fill: '색상명', stroke: '색상명' 추가
   * - style={{fill: 'none'}} or style={{stroke: 'none'}} or style={{stroke: '#aaaaaa'}}
   * ***
   * @key name + IconType
   * @value IconWrap > svg tag
   * */
  const svgIcons: {[key: string]: ReactElement<any, any> | null} = {
    allowTop: (
      <IconWrap iconColor={iconColor}>
        <svg viewBox='0 0 11 7' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path
            d='M9.50977 5.35303L5.10224 1.00021L0.861671 5.35303'
            stroke='white'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    allowBottom: (
      <IconWrap iconColor={iconColor}>
        <svg viewBox='0 0 11 7' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path
            d='M0.986328 1.59082L5.39385 5.94364L9.63442 1.59082'
            stroke={Colors.common.light_blue}
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    allowLeft: (
      <IconWrap iconColor={iconColor}>
        <svg viewBox='0 0 9 13' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path
            d='M7.34448 1L1.2757 6.38916L7.34448 11.6121'
            stroke='white'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    allowRight: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='7'
          height='11'
          viewBox='0 0 7 11'
          fill='none'
        >
          <path
            d='M1.07104 10.0001L5.46037 5.6109L1.07104 1.388'
            stroke={Colors.common.light_blue}
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    settingFill: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          viewBox='0 0 24 24'
        >
          <path d='M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z' />
        </svg>
      </IconWrap>
    ),
    allowRightThin: (
      <IconWrap iconColor={iconColor}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='7'
          height='11'
          viewBox='0 0 7 11'
          style={{fill: 'none'}}
        >
          <path
            d='M1 0.5L6 5.5L1 10.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{fill: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    edfSolid: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='20'
          height='14'
          viewBox='0 0 20 14'
          style={{fill: 'none', stroke: 'none'}}
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M6.87523 8.42026H9.44489L14.5842 8.42026L16.0781 8.42026C17.0314 8.42026 17.9846 8.09867 18.4612 7.15223C19.1316 5.82099 18.9379 4.31291 17.9846 3.36647C16.6593 2.05065 14.8697 2.7228 14.1559 3.02479C14.1559 3.02479 14.3932 0.96913 12.2652 1.00035C10.5504 1.02551 10.1587 2.4208 10.3014 3.02479C10.3014 3.02479 9.07592 2.20235 7.97558 2.89325C6.87523 3.58415 7.30351 4.53104 7.30351 4.83676C6.73247 4.68577 5.39761 4.71553 5.11581 6.20578C4.834 7.69604 6.16144 8.26926 6.87523 8.42026Z'
            fill='white'
          />
          <path
            d='M9.44489 8.42026L9.44489 7.92026H9.44489V8.42026ZM6.87523 8.42026L6.77175 8.90943C6.80577 8.91663 6.84045 8.92026 6.87523 8.92026V8.42026ZM5.11581 6.20578L5.6071 6.29869L5.11581 6.20578ZM7.30351 4.83676L7.17569 5.32015C7.32569 5.35982 7.48561 5.3276 7.60854 5.23294C7.73148 5.13829 7.80351 4.99192 7.80351 4.83676H7.30351ZM7.97558 2.89325L8.24146 3.3167L7.97558 2.89325ZM10.3014 3.02479L10.0228 3.43997C10.1936 3.55458 10.4171 3.55292 10.5862 3.43578C10.7553 3.31865 10.8353 3.10994 10.788 2.90978L10.3014 3.02479ZM12.2652 1.00035L12.2725 1.5003L12.2652 1.00035ZM14.1559 3.02479L13.6592 2.96746C13.6388 3.14417 13.7139 3.31841 13.8562 3.42502C13.9986 3.53164 14.1869 3.55459 14.3508 3.48528L14.1559 3.02479ZM17.9846 3.36647L18.3369 3.01166H18.3369L17.9846 3.36647ZM16.0781 8.42026V7.92026H16.0781L16.0781 8.42026ZM16.2973 8.92026C16.5735 8.92026 16.7973 8.6964 16.7973 8.42026C16.7973 8.14411 16.5735 7.92026 16.2973 7.92026V8.92026ZM14.5842 8.42026L14.5842 8.92026H14.5842L14.5842 8.42026ZM18.4612 7.15223L18.9078 7.37712L18.4612 7.15223ZM9.44489 7.92026H6.87523V8.92026H9.44489V7.92026ZM6.97871 7.93108C6.66946 7.86566 6.25357 7.71174 5.95728 7.44416C5.68811 7.20108 5.50165 6.85632 5.6071 6.29869L4.62451 6.11288C4.44816 7.0455 4.78451 7.73248 5.28705 8.18632C5.76248 8.61567 6.3672 8.82385 6.77175 8.90943L6.97871 7.93108ZM5.6071 6.29869C5.71601 5.72276 6.00373 5.48249 6.28573 5.37051C6.60402 5.24412 6.96734 5.26506 7.17569 5.32015L7.43133 4.35338C7.06865 4.25748 6.47901 4.2178 5.91667 4.4411C5.31802 4.67882 4.79741 5.19855 4.62451 6.11288L5.6071 6.29869ZM7.80351 4.83676C7.80351 4.75182 7.78966 4.66597 7.78025 4.61038C7.76716 4.53305 7.75953 4.49696 7.74742 4.42177C7.7271 4.29556 7.71489 4.17306 7.72515 4.05147C7.74279 3.84221 7.82819 3.57619 8.24146 3.3167L7.7097 2.46981C7.02262 2.90122 6.77198 3.45409 6.72868 3.96743C6.70847 4.20714 6.73456 4.42186 6.76015 4.58077C6.771 4.64814 6.78823 4.74159 6.79427 4.77725C6.80398 4.83466 6.80351 4.84528 6.80351 4.83676H7.80351ZM8.24146 3.3167C8.63165 3.0717 9.05482 3.08434 9.42143 3.18282C9.60299 3.23159 9.75849 3.29874 9.86844 3.35394C9.92291 3.38128 9.96484 3.40501 9.99167 3.42091C10.005 3.42884 10.0146 3.43476 10.0199 3.43813C10.0225 3.43982 10.0241 3.44086 10.0247 3.44119C10.0249 3.44135 10.0249 3.44134 10.0246 3.44114C10.0244 3.44104 10.0242 3.44089 10.0239 3.4407C10.0238 3.4406 10.0236 3.44049 10.0234 3.44037C10.0233 3.44031 10.0232 3.44021 10.0231 3.44018C10.023 3.44008 10.0228 3.43997 10.3014 3.02479C10.5801 2.60962 10.5799 2.60951 10.5797 2.60939C10.5797 2.60935 10.5795 2.60923 10.5794 2.60914C10.5791 2.60897 10.5788 2.60879 10.5785 2.60859C10.5779 2.6082 10.5773 2.60775 10.5765 2.60726C10.575 2.60627 10.5732 2.60509 10.5711 2.60372C10.5669 2.60098 10.5615 2.59749 10.5549 2.59332C10.5418 2.58498 10.5239 2.57391 10.5016 2.5607C10.4571 2.53432 10.3947 2.49919 10.3171 2.46023C10.1629 2.38281 9.94322 2.28753 9.68085 2.21705C9.15961 2.07704 8.41985 2.0239 7.7097 2.46981L8.24146 3.3167ZM10.788 2.90978C10.7513 2.75423 10.7788 2.39807 10.9985 2.07913C11.1967 1.79133 11.5692 1.51062 12.2725 1.5003L12.2578 0.500406C11.2464 0.515246 10.5656 0.944764 10.175 1.51189C9.80577 2.04789 9.70886 2.69137 9.81485 3.1398L10.788 2.90978ZM12.2725 1.5003C12.7219 1.49371 13.0005 1.5968 13.1772 1.71699C13.3553 1.83805 13.4703 2.00384 13.5452 2.18866C13.6215 2.3771 13.6515 2.57499 13.6606 2.73266C13.665 2.80991 13.6642 2.87343 13.6624 2.91578C13.6615 2.93685 13.6604 2.95237 13.6597 2.96137C13.6593 2.96586 13.6591 2.9687 13.659 2.96976C13.6589 2.97029 13.6589 2.97038 13.6589 2.97C13.659 2.96981 13.659 2.96951 13.659 2.96908C13.6591 2.96887 13.6591 2.96863 13.6591 2.96836C13.6591 2.96823 13.6592 2.968 13.6592 2.96793C13.6592 2.9677 13.6592 2.96746 14.1559 3.02479C14.6526 3.08213 14.6527 3.08187 14.6527 3.08161C14.6527 3.08151 14.6527 3.08124 14.6528 3.08104C14.6528 3.08064 14.6528 3.08022 14.6529 3.07976C14.653 3.07884 14.6531 3.07779 14.6532 3.07661C14.6535 3.07426 14.6538 3.07139 14.6541 3.06803C14.6548 3.06132 14.6556 3.05262 14.6564 3.04206C14.6582 3.02097 14.6601 2.99235 14.6615 2.95727C14.6644 2.88733 14.6655 2.79049 14.6589 2.6755C14.646 2.44872 14.6023 2.13464 14.472 1.81307C14.3402 1.48787 14.1154 1.1456 13.7395 0.890009C13.3622 0.633546 12.8725 0.491388 12.2578 0.500406L12.2725 1.5003ZM14.3508 3.48528C14.696 3.33921 15.2569 3.12418 15.8703 3.08809C16.4698 3.05283 17.0954 3.18821 17.6323 3.72129L18.3369 3.01166C17.5485 2.22891 16.6167 2.04246 15.8116 2.08982C15.0205 2.13635 14.3297 2.40839 13.9611 2.56431L14.3508 3.48528ZM16.0781 8.92026H16.2973V7.92026H16.0781V8.92026ZM16.0781 7.92026L14.5842 7.92026L14.5842 8.92026L16.0781 8.92026L16.0781 7.92026ZM17.6323 3.72129C18.4119 4.49529 18.6011 5.76281 18.0147 6.92734L18.9078 7.37712C19.6622 5.87917 19.4638 4.13053 18.3369 3.01166L17.6323 3.72129ZM18.0147 6.92734C17.6575 7.63661 16.9339 7.92026 16.0781 7.92026V8.92026C17.1289 8.92026 18.3118 8.56073 18.9078 7.37712L18.0147 6.92734ZM9.44489 8.92026L14.5842 8.92026L14.5842 7.92026L9.44489 7.92026L9.44489 8.92026Z'
            fill='white'
          />
          <path
            d='M11.9673 9.47949V11.3729'
            stroke='white'
            strokeLinecap='round'
          />
          <path
            d='M12.4673 6.2998C12.4673 6.02366 12.2434 5.7998 11.9673 5.7998C11.6911 5.7998 11.4673 6.02366 11.4673 6.2998H12.4673ZM11.4673 8.9499V9.4499H12.4673V8.9499H11.4673ZM11.4673 6.2998V8.9499H12.4673V6.2998H11.4673Z'
            style={{fill: '#15243B', stroke: '#15243B'}}
          />
          <path
            d='M10.3774 11.0703L11.9329 12.6604L13.5576 11.0703'
            stroke='white'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </IconWrap>
    ),
    eegSolid: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='14'
          height='15'
          viewBox='0 0 14 15'
          style={{fill: 'none'}}
          xmlns='http://www.w3.org/2000/svg'
        >
          <rect
            x='7.57031'
            y='10.0977'
            width='2.81178'
            height='5.40727'
            rx='1.40589'
            transform='rotate(-41.9727 7.57031 10.0977)'
            fill='white'
          />
          <circle
            cx='6.40727'
            cy='5.47075'
            r='4.90727'
            style={{stroke: '#15243B'}}
          />
          <path
            d='M10.3145 5.40727C10.3145 8.11748 8.11748 10.3145 5.40727 10.3145C2.69706 10.3145 0.5 8.11748 0.5 5.40727C0.5 2.69706 2.69706 0.5 5.40727 0.5C8.11748 0.5 10.3145 2.69706 10.3145 5.40727Z'
            fill='white'
            stroke='white'
          />
          <path
            d='M2.16309 6.4889L3.68984 5.04696L4.70768 6.4889L6.74336 4.08567L7.7612 5.52761L10.8147 2.16309'
            style={{stroke: '#15243B'}}
            strokeWidth='0.8'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </IconWrap>
    ),
    hrvSolid: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='17'
          height='14'
          viewBox='0 0 17 14'
          style={{fill: 'none'}}
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M8.67938 2.66245C8.67938 2.66245 9.74546 1.14733 11.1795 1.02963C11.1795 1.02963 12.9277 0.720092 14.1855 2.10189C14.1855 2.10189 15.7561 3.38826 15.0663 6.05311C15.0663 6.05311 15.0877 6.91158 12.5722 9.38824L8.82201 13L4.24195 8.71098C4.24195 8.71098 1.84374 6.70245 2.11747 4.50574C2.11747 4.50574 2.17068 1.63475 4.8282 1.09247C4.8282 1.09247 7.02533 0.454545 8.67938 2.66245Z'
            style={{fill: iconColor, stroke: iconColor}}
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M1 8.26811H5.65999L6.97705 4.92773L8.66544 9.29137L10.2865 5.86049L11.1307 8.23764H16.3366'
            style={{stroke: iconColor}}
            strokeWidth='0.8'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M1 7.61381H5.65999L6.97705 4.27344L8.66544 8.63707L10.2865 5.2062L11.1307 7.58335H16.3366'
            style={{stroke: '#15243B'}}
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </IconWrap>
    ),
    therapySolid: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='20'
          height='18'
          viewBox='0 0 20 18'
          style={{fill: 'none'}}
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M5 12L4 11L3.5 10.5L3 9.5L3.5 8V7.5L5 6.5H6.5L7 6L8 4.5L8.5 4H11L12 4.5L12.5 5.5H13H14L15 6L16 6.5L16.5 7.5V9.5V10L17 11L17.5 12L17 13L16 14.5L11.77 17.46L11.5 16.5V15L10.5 14.5L10 14H7.5L7 13L6.5 12H6H5Z'
            style={{fill: iconColor}}
          />
          <path
            d='M11.5755 17.0651V15.9401C11.5755 14.815 10.6644 14.14 9.55055 14.14C7.52541 14.14 5.95036 13.465 6.40037 11.4399C6.50883 10.965 7.12331 10.0899 8.42542 10.0899M6.17528 12.2499C4.15022 12.0249 2.35014 10.2964 3.25028 7.97474C3.66506 6.90495 5.12957 6.01286 6.62528 6.17479M6.85031 7.07471C6.85031 5.84869 7.0753 3.92461 9.53805 3.58632C10.4051 3.46722 12.3454 3.74275 12.8662 5.61521C14.1683 5.28082 16.9633 5.61146 16.5133 8.76156C16.5133 8.98452 16.2883 9.66158 16.0064 10.2964M16.5 10C17.0283 10.2964 17.8602 11.2318 17.4236 12.6369C17 14 15.6256 14.365 14.5006 14.14C13.3756 13.915 12.7006 12.4431 12.7006 11.4399M13.8428 8.62445C13.9513 9.18171 13.973 10.43 13.1917 10.965C12.2152 11.6338 10.9132 10.9652 10.5875 10.2964M8.63436 7.62142C8.85138 7.17555 9.61094 6.41756 10.9131 6.95261C11.3471 7.06407 12.1501 7.62135 11.8896 8.95882'
            style={{stroke: iconColor}}
            strokeWidth='0.892527'
            strokeLinecap='round'
          />
          <circle cx='1.67502' cy='6.49045' r='0.675021' fill='white' />
          <circle cx='5.72531' cy='3.34006' r='0.675021' fill='white' />
          <circle cx='10.4499' cy='1.67502' r='0.675021' fill='white' />
          <circle cx='18.3249' cy='6.48947' r='0.675021' fill='white' />
          <circle cx='15.1755' cy='3.34006' r='0.675021' fill='white' />
          <path
            d='M13.8427 8.625C13.9512 9.18226 13.9729 10.4305 13.1916 10.9656C12.215 11.6344 10.913 10.9657 10.5874 10.2969'
            style={{stroke: '#15243B'}}
            strokeWidth='0.892527'
            strokeLinecap='round'
          />
          <path
            d='M8.63428 7.62139C8.8513 7.17552 9.61086 6.41753 10.913 6.95258C11.347 7.06404 12.1501 7.62132 11.8895 8.95879'
            style={{stroke: '#15243B'}}
            strokeWidth='0.892527'
            strokeLinecap='round'
          />
          <path
            d='M7.03964 14.4922C6.62529 14.2198 5.97103 13.3693 6.66873 12.1466C6.87212 11.7901 7.49067 11.0055 8.78366 11.4355'
            style={{stroke: '#15243B'}}
            strokeWidth='0.892527'
            strokeLinecap='round'
          />
          <path
            d='M19.0004 12.0006C19.281 12.4156 18.6253 13.3305 17.5 14.2003C17.125 14.4901 16 15.0005 14.6 14.3005'
            style={{stroke: '#15243B'}}
            strokeWidth='0.892527'
            strokeLinecap='round'
          />
        </svg>
      </IconWrap>
    ),
    personSolid: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          style={{fill: 'none'}}
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M8.99488 8.99953C10.5402 8.99953 11.793 7.74677 11.793 6.20142C11.793 4.65607 10.5402 3.40332 8.99488 3.40332C7.44953 3.40332 6.19678 4.65607 6.19678 6.20142C6.19678 7.74677 7.44953 8.99953 8.99488 8.99953Z'
            style={{fill: iconColor, stroke: iconColor}}
            strokeMiterlimit='10'
          />
          <path
            d='M4.31473 10.7345C4.65057 9.93428 5.47554 8.99902 5.47554 8.99902C6.58052 10.3048 9.53894 12.1329 12.5328 8.99902C14.1517 10.8879 14.0294 12.3469 14.0294 12.3469C14.0463 13.215 13.4783 13.499 13.4783 13.499C13.1273 13.7446 12.2262 14.1132 12.2262 14.1132C9.44643 15.0654 6.63636 14.313 6.63636 14.313C4.83417 13.8828 4.3147 13.2229 4.3147 13.2229C3.95832 12.9164 3.96338 12.2395 3.96338 12.2395C3.96717 11.6597 4.31473 10.7345 4.31473 10.7345Z'
            style={{stroke: iconColor, fill: 'none'}}
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M5.47554 8.99902C5.47554 8.99902 4.65057 9.93428 4.31473 10.7345C4.31473 10.7345 3.96717 11.6597 3.96338 12.2395C3.96338 12.2395 3.95832 12.9164 4.3147 13.2229C4.3147 13.2229 4.83417 13.8828 6.63636 14.313C6.63636 14.313 9.44643 15.0654 12.2262 14.1132C12.2262 14.1132 13.1273 13.7446 13.4783 13.499C13.4783 13.499 14.0463 13.215 14.0294 12.3469C14.0294 12.3469 14.1517 10.8879 12.5328 8.99902'
            style={{stroke: iconColor, fill: 'none'}}
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M8.99997 10.6778C8.55227 10.6778 6.57494 9.93167 5.64224 9.55859L5.08262 10.1182L4.523 10.6778L3.96338 12.3567L4.523 13.4759L7.88072 14.5952L10.6788 14.0356L13.4769 13.4759L14.0366 12.3567L13.4769 11.7971L12.9173 9.55859H11.7981V10.1182C11.0519 10.3048 9.44766 10.6778 8.99997 10.6778Z'
            style={{fill: iconColor}}
          />
        </svg>
      </IconWrap>
    ),
    hospitalSolid: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='19'
          height='19'
          viewBox='0 0 19 19'
          style={{fill: 'none'}}
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M14.5582 5.58301H13.4165V15.6534H15.6184C15.6455 12.9728 15.6836 7.48118 15.6184 6.66975C15.5531 5.85831 14.8844 5.60716 14.5582 5.58301Z'
            style={{fill: iconColor, stroke: iconColor}}
            strokeWidth='1.05556'
          />
          <path
            d='M4.44228 5.58301H5.58398V15.6534H3.38213C3.35495 12.9728 3.31689 7.48118 3.38213 6.66975C3.44737 5.85831 4.11608 5.60716 4.44228 5.58301Z'
            style={{fill: iconColor, stroke: iconColor}}
            strokeWidth='1.05556'
          />
          <path
            d='M5.58398 4.28732C5.58398 3.58783 6.08919 3.36812 6.34179 3.3457H12.5306C13.2884 3.3457 13.4357 4.06313 13.4147 4.42184V15.654H10.8886V13.5017H7.98371V15.654H5.58398V4.28732Z'
            style={{fill: iconColor, stroke: iconColor}}
            strokeWidth='1.05556'
          />
          <path
            d='M9.64015 4.74414V6.00294V7.26175M10.899 5.97497H9.64015H8.38135'
            style={{stroke: '#15243B'}}
            strokeWidth='0.844451'
          />
          <rect
            x='8.94092'
            y='8.94043'
            width='1.11894'
            height='0.745958'
            style={{fill: '#15243B', stroke: 'none'}}
          />
          <rect
            x='8.94092'
            y='10.4326'
            width='1.11894'
            height='0.745958'
            style={{fill: '#15243B', stroke: 'none'}}
          />
          <rect
            x='11.1787'
            y='8.94043'
            width='1.11894'
            height='0.745958'
            style={{fill: '#15243B', stroke: 'none'}}
          />
          <rect
            x='11.1787'
            y='10.4326'
            width='1.11894'
            height='0.745958'
            style={{fill: '#15243B', stroke: 'none'}}
          />
          <rect
            x='6.70312'
            y='8.94043'
            width='1.11894'
            height='0.745958'
            style={{fill: '#15243B', stroke: 'none'}}
          />
          <rect
            x='6.70312'
            y='10.4326'
            width='1.11894'
            height='0.745958'
            style={{fill: '#15243B', stroke: 'none'}}
          />
        </svg>
      </IconWrap>
    ),
    peopleSolid: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          style={{fill: 'none'}}
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M9.01841 3.69943L11.1385 2.10938H12.1985H13.2586H13.7886L14.8486 2.63939L15.9086 3.69943V4.75947V6.34952V7.93958L14.8486 10.5897L9.54842 14.8298L7.95837 14.2998L2.65818 9.52964L1.59814 5.81951L2.65818 3.16941L4.24824 2.10938H5.30828H6.89833L7.95837 2.63939L8.48839 3.16941L9.01841 3.69943Z'
            style={{fill: '#15243B'}}
          />
          <mask
            id='mask0_3935_6722'
            style={{maskType: 'alpha'}}
            maskUnits='userSpaceOnUse'
            x='2'
            y='2'
            width='14'
            height='15'
          >
            <path
              d='M8.87705 4.97539C8.87705 4.97539 9.90865 3.39401 11.2963 3.27116C11.2963 3.27116 12.9879 2.94809 14.205 4.39031C14.205 4.39031 15.7248 5.73293 15.0573 8.51431C15.0573 8.51431 15.6631 9.75008 13.1067 11.8562L9.30286 15.6024L4.58317 11.2884C4.58317 11.2884 2.26253 9.19205 2.52741 6.89928C2.52741 6.89928 2.66535 4.0865 5.15045 3.33675C5.15045 3.33675 7.11751 2.73247 8.87705 4.97539Z'
              style={{fill: iconColor, stroke: '#231815'}}
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </mask>
          <g mask='url(#mask0_3935_6722)'>
            <path
              d='M10.8979 9.9471C10.8979 9.05782 11.6189 8.33691 12.5081 8.33691C13.3974 8.33691 14.1183 9.05782 14.1183 9.9471V12.2228C14.1183 13.1121 13.3974 13.833 12.5081 13.833C11.6189 13.833 10.8979 13.1121 10.8979 12.2228V9.9471Z'
              style={{fill: iconColor, stroke: 'none'}}
            />
            <path
              d='M4.6084 10.2225C4.6084 9.33321 5.3293 8.6123 6.21858 8.6123C7.10786 8.6123 7.82877 9.33321 7.82877 10.2225V12.4982C7.82877 13.3875 7.10786 14.1084 6.21858 14.1084C5.3293 14.1084 4.6084 13.3875 4.6084 12.4982V10.2225Z'
              style={{fill: iconColor, stroke: 'none'}}
            />
            <path
              d='M7.20898 10.127C7.20898 8.99654 8.12544 8.08008 9.25595 8.08008C10.3865 8.08008 11.3029 8.99654 11.3029 10.127V13.8798C11.3029 15.0103 10.3865 15.9268 9.25595 15.9268C8.12544 15.9268 7.20898 15.0103 7.20898 13.8798V10.127Z'
              style={{fill: iconColor, stroke: 'none'}}
            />
            <path
              d='M13.6103 7.70486C13.6103 8.2813 13.1722 8.74859 12.6318 8.74859C12.0914 8.74859 11.6533 8.2813 11.6533 7.70486C11.6533 7.12843 12.0914 6.66113 12.6318 6.66113C13.1722 6.66113 13.6103 7.12843 13.6103 7.70486Z'
              style={{fill: iconColor, stroke: 'none'}}
            />
            <path
              d='M7.08688 8.03007C7.08688 8.6065 6.64879 9.0738 6.10838 9.0738C5.56797 9.0738 5.12988 8.6065 5.12988 8.03007C5.12988 7.45363 5.56797 6.98634 6.10838 6.98634C6.64879 6.98634 7.08688 7.45363 7.08688 8.03007Z'
              style={{fill: iconColor, stroke: 'none'}}
            />
            <path
              d='M10.6996 7.05638C10.6996 7.92103 10.0425 8.59161 9.2319 8.59161C8.42129 8.59161 7.76416 7.92103 7.76416 7.05638C7.76416 6.19173 8.42129 5.60645 9.2319 5.60645C10.0425 5.60645 10.6996 6.19173 10.6996 7.05638Z'
              style={{fill: iconColor, stroke: 'none'}}
            />
          </g>
          <path
            d='M9.06767 4.0689C9.06767 4.0689 10.0928 2.38922 11.7517 2.16791C11.7517 2.16791 13.8253 1.72525 15.2769 3.27451C15.2769 3.27451 17.1434 4.82376 16.1065 8.14359C16.1065 8.14359 15.4843 10.3568 13.2433 12.2025L9.22067 15.8899L6.56757 13.4553L4.07919 11.2421C4.07919 11.2421 1.38344 9.02887 1.59099 6.17013C1.59099 6.17013 1.79817 2.83186 4.93673 2.27363C4.93673 2.27363 7.39703 1.72525 9.06767 4.0689Z'
            style={{stroke: iconColor, fill: 'none'}}
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </IconWrap>
    ),
    displayDescriptionSolid: (
      <svg
        width='18'
        height='19'
        viewBox='0 0 18 19'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M9.01828 4.6174L11.1384 3.02734H12.1984H13.2584H13.7885L14.8485 3.55736L15.9085 4.6174V5.67744V7.26749V8.85755L14.8485 11.5076L9.5483 15.7478L7.95825 15.2178L2.65806 10.4476L1.59802 6.73747L2.65806 4.08738L4.24812 3.02734H5.30815H6.89821L7.95825 3.55736L8.48827 4.08738L9.01828 4.6174Z'
          fill='#15243B'
        />
        <path
          d='M2 15.9771L5.0438 10.9766L6.23958 12.3898L8.52243 13.6942L5.80475 18.3686L5.0438 15.3248L2 15.9771Z'
          fill='#DDE7F5'
        />
        <path
          d='M15.4795 15.9771L12.4357 10.9766L11.2399 12.3898L8.95706 13.6942L11.6747 18.3686L12.4357 15.3248L15.4795 15.9771Z'
          fill='#DDE7F5'
        />
        <circle cx='8.84865' cy='7.71584' r='6.19631' fill='white' />
        <path
          d='M13.0496 7.69922C13.0496 10.0188 11.1692 11.8992 8.84961 11.8992C6.53001 11.8992 4.64961 10.0188 4.64961 7.69922C4.64961 5.37962 6.53001 3.49922 8.84961 3.49922C11.1692 3.49922 13.0496 5.37962 13.0496 7.69922Z'
          stroke='#353E56'
          strokeWidth='0.6'
        />
        <path
          d='M9.29461 5.35186L9.29461 5.35186L9.8498 6.47644L11.0471 6.67926L11.0471 6.67927L11.0438 6.69898C11.1207 6.71182 11.1928 6.74661 11.2519 6.79949C11.3111 6.85236 11.355 6.92127 11.3789 6.99858L9.29461 5.35186ZM9.29461 5.35186C9.25737 5.27647 9.20088 5.21313 9.13123 5.16885C9.06157 5.12456 8.98148 5.10109 8.89975 5.10109C8.81802 5.10109 8.73793 5.12456 8.66827 5.16885C8.59862 5.21313 8.54213 5.27647 8.50489 5.35186L8.50489 5.35186M9.29461 5.35186L8.50489 5.35186M8.50489 5.35186L7.94971 6.47644L6.75241 6.67927L6.75241 6.67927M8.50489 5.35186L6.75241 6.67927M6.75241 6.67927C6.67176 6.69295 6.59638 6.72951 6.53451 6.78483C6.47264 6.84014 6.42668 6.91206 6.40156 6.99265C6.37644 7.07323 6.3731 7.15945 6.39191 7.24186C6.41071 7.32427 6.45095 7.39979 6.5083 7.4601L6.50831 7.4601M6.75241 6.67927L6.50831 7.4601M6.50831 7.4601L7.36304 8.35825M6.50831 7.4601L7.36304 8.35825M7.36304 8.35825L7.17801 9.60872L7.36304 8.35825ZM7.58091 10.1396C7.66241 10.1464 7.7441 10.1297 7.81698 10.0912L7.20668 9.85648C7.23793 9.93479 7.28933 10.0027 7.35534 10.0527C7.42135 10.1027 7.4994 10.1328 7.58091 10.1396Z'
          fill='#353E56'
          stroke='#353E56'
          strokeWidth='0.04'
        />
      </svg>
    ),

    salesMenuSolid: (
      <IconWrap iconColor={iconColor}>
        <svg
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <mask id='path-1-inside-1_1101_1380' fill='white'>
            <rect
              x='3.19092'
              y='10.3979'
              width='3.24247'
              height='4.8637'
              rx='0.5'
            />
          </mask>
          <rect
            x='3.19092'
            y='10.3979'
            width='3.24247'
            height='4.8637'
            rx='0.5'
            fill='#10CFC9'
            stroke='#10CFC9'
            strokeWidth='2'
            mask='url(#path-1-inside-1_1101_1380)'
          />
          <path
            d='M7.91016 8.88184L10.0903 8.88184L10.0903 14.7617L7.91016 14.7617L7.91016 8.88184Z'
            fill='#10CFC9'
            stroke='#10CFC9'
          />
          <mask id='path-3-inside-2_1101_1380' fill='white'>
            <rect
              x='11.5669'
              y='6.34473'
              width='3.24247'
              height='8.91679'
              rx='0.5'
            />
          </mask>
          <rect
            x='11.5669'
            y='6.34473'
            width='3.24247'
            height='8.91679'
            rx='0.5'
            fill='#10CFC9'
            stroke='#10CFC9'
            strokeWidth='2'
            mask='url(#path-3-inside-2_1101_1380)'
          />
          <line
            x1='2.55986'
            y1='14.8117'
            x2='15.4403'
            y2='14.8117'
            stroke='#10CFC9'
            strokeWidth='0.9'
            strokeLinecap='round'
          />
          <path
            d='M5.29752 7.8618C5.29752 8.26775 4.96843 8.59684 4.56248 8.59684C4.15653 8.59684 3.82744 8.26775 3.82744 7.8618C3.82744 7.45585 4.15653 7.12676 4.56248 7.12676C4.96843 7.12676 5.29752 7.45585 5.29752 7.8618ZM9.62081 5.7417C9.62081 6.14765 9.29172 6.47674 8.88577 6.47674C8.47982 6.47674 8.15073 6.14765 8.15073 5.7417C8.15073 5.33575 8.47982 5.00666 8.88577 5.00666C9.29172 5.00666 9.62081 5.33575 9.62081 5.7417ZM13.9025 3.79832C13.9025 4.20427 13.5734 4.53336 13.1675 4.53336C12.7615 4.53336 12.4324 4.20427 12.4324 3.79832C12.4324 3.39237 12.7615 3.06328 13.1675 3.06328C13.5734 3.06328 13.9025 3.39237 13.9025 3.79832Z'
            fill='#353E56'
            stroke='#10CFC9'
            strokeWidth='0.65'
          />
          <g filter='url(#filter0_d_1101_1380)'>
            <path
              d='M5.4873 7.42509L7.91916 6.20917M9.81059 5.26345L12.2424 4.18262'
              stroke='#10CFC9'
              strokeWidth='0.7'
              strokeLinecap='square'
            />
          </g>
          <path
            d='M2.65039 7.75098C2.45709 7.75098 2.30039 7.90768 2.30039 8.10098C2.30039 8.29428 2.45709 8.45098 2.65039 8.45098V7.75098ZM3.54207 8.45098H3.89207V7.75098H3.54207V8.45098ZM15.0798 3.99258C15.2731 3.99258 15.4298 3.83588 15.4298 3.64258C15.4298 3.44928 15.2731 3.29258 15.0798 3.29258V3.99258ZM14.2692 3.29258H13.9192V3.99258H14.2692V3.29258ZM2.65039 8.45098H3.54207V7.75098H2.65039V8.45098ZM15.0798 3.29258H14.2692V3.99258H15.0798V3.29258Z'
            fill='#10CFC9'
          />
          <defs>
            <filter
              id='filter0_d_1101_1380'
              x='1.01758'
              y='3.7207'
              width='15.687'
              height='12.1738'
              filterUnits='userSpaceOnUse'
              colorInterpolationFilters='sRGB'
            >
              <feFlood floodOpacity='0' result='BackgroundImageFix' />
              <feColorMatrix
                in='SourceAlpha'
                type='matrix'
                values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                result='hardAlpha'
              />
              <feOffset dy='4' />
              <feGaussianBlur stdDeviation='2' />
              <feComposite in2='hardAlpha' operator='out' />
              <feColorMatrix
                type='matrix'
                values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0'
              />
              <feBlend
                mode='normal'
                in2='BackgroundImageFix'
                result='effect1_dropShadow_1101_1380'
              />
              <feBlend
                mode='normal'
                in='SourceGraphic'
                in2='effect1_dropShadow_1101_1380'
                result='shape'
              />
            </filter>
          </defs>
        </svg>
      </IconWrap>
    ),
    checkedFill: (
      <svg
        width='20'
        height='20'
        viewBox='0 0 20 20'
        style={{fill: 'none'}}
        xmlns='http://www.w3.org/2000/svg'
      >
        <circle cx='10' cy='10' r='10' style={{fill: 'none'}} />
        <path
          d='M8.07061 14.3332C7.87061 14.3332 7.68726 14.2665 7.53726 14.1165L4.22061 10.7999C3.92061 10.4999 3.92061 10.0332 4.22061 9.73321C4.52061 9.43321 4.98726 9.43321 5.28726 9.73321L8.08727 12.5165L14.7373 5.8832C15.0373 5.5832 15.5039 5.5832 15.8039 5.8832C16.1039 6.1832 16.1039 6.64987 15.8039 6.94987L8.6206 14.1165C8.45393 14.2665 8.27061 14.3332 8.07061 14.3332Z'
          style={{fill: '#32C7C9'}}
        />
      </svg>
    ),
    notCheckedFill: (
      <svg
        width='20'
        height='20'
        viewBox='0 0 20 20'
        style={{fill: 'none'}}
        xmlns='http://www.w3.org/2000/svg'
      >
        <circle
          cx='10'
          cy='10'
          r='10'
          style={{fill: Colors.common.grays.g_14}}
        />
        <path
          d='M8.07061 14.3332C7.87061 14.3332 7.68726 14.2665 7.53726 14.1165L4.22061 10.7999C3.92061 10.4999 3.92061 10.0332 4.22061 9.73321C4.52061 9.43321 4.98726 9.43321 5.28726 9.73321L8.08727 12.5165L14.7373 5.8832C15.0373 5.5832 15.5039 5.5832 15.8039 5.8832C16.1039 6.1832 16.1039 6.64987 15.8039 6.94987L8.6206 14.1165C8.45393 14.2665 8.27061 14.3332 8.07061 14.3332Z'
          style={{fill: '#fff'}}
        />
      </svg>
    ),
    gridCheckedFill: (
      <svg
        width='20'
        height='20'
        viewBox='0 0 20 20'
        style={{fill: 'none'}}
        xmlns='http://www.w3.org/2000/svg'
      >
        <circle cx='10' cy='10' r='10' style={{fill: '#32C7C9'}} />
        <path
          d='M8.07061 14.3332C7.87061 14.3332 7.68726 14.2665 7.53726 14.1165L4.22061 10.7999C3.92061 10.4999 3.92061 10.0332 4.22061 9.73321C4.52061 9.43321 4.98726 9.43321 5.28726 9.73321L8.08727 12.5165L14.7373 5.8832C15.0373 5.5832 15.5039 5.5832 15.8039 5.8832C16.1039 6.1832 16.1039 6.64987 15.8039 6.94987L8.6206 14.1165C8.45393 14.2665 8.27061 14.3332 8.07061 14.3332Z'
          style={{fill: '#fff'}}
        />
      </svg>
    ),
    erroredFill: (
      <svg
        width='21'
        height='21'
        viewBox='0 0 21 21'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <circle
          cx='10.39'
          cy='10.6603'
          r='10'
          transform='rotate(60 10.39 10.6603)'
          fill='#EE5454'
        />
        <path
          d='M15 6.24951C15 6.24951 14.4375 6.81201 10.8215 10.428C7.20546 14.0441 6.00012 15.2494 6.00012 15.2494'
          stroke='white'
          strokeWidth='1.4'
        />
        <path
          d='M6.00049 6.24951C6.00049 6.24951 6.56299 6.81201 10.179 10.428C13.795 14.0441 15.0004 15.2494 15.0004 15.2494'
          stroke='white'
          strokeWidth='1.4'
        />
      </svg>
    ),
    erroredGrayFill: (
      <svg
        width='21'
        height='21'
        viewBox='0 0 21 21'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <circle
          cx='10.39'
          cy='10.6603'
          r='10'
          transform='rotate(60 10.39 10.6603)'
          style={{fill: Colors.common.grays.g_14}}
        />
        <path
          d='M15 6.24951C15 6.24951 14.4375 6.81201 10.8215 10.428C7.20546 14.0441 6.00012 15.2494 6.00012 15.2494'
          stroke='white'
          strokeWidth='1.4'
        />
        <path
          d='M6.00049 6.24951C6.00049 6.24951 6.56299 6.81201 10.179 10.428C13.795 14.0441 15.0004 15.2494 15.0004 15.2494'
          stroke='white'
          strokeWidth='1.4'
        />
      </svg>
    ),
    erroredNotFill: (
      <svg
        width='21'
        height='21'
        viewBox='0 0 21 21'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <circle
          cx='10.39'
          cy='10.6603'
          r='10'
          transform='rotate(60 10.39 10.6603)'
          style={{fill: 'none', stroke: 'none'}}
        />
        <path
          d='M15 6.24951C15 6.24951 14.4375 6.81201 10.8215 10.428C7.20546 14.0441 6.00012 15.2494 6.00012 15.2494'
          style={{fill: 'none', stroke: '#EE5454'}}
          strokeWidth='1.4'
        />
        <path
          d='M6.00049 6.24951C6.00049 6.24951 6.56299 6.81201 10.179 10.428C13.795 14.0441 15.0004 15.2494 15.0004 15.2494'
          style={{fill: 'none', stroke: '#EE5454'}}
          strokeWidth='1.4'
        />
      </svg>
    ),
  }

  if (svgIcons[name] === undefined) {
    console.log(
      `**${name}**이 **${svgIcons[name]}**입니다. SvgIcon의 props name, iconType을 확인하고 없으면 추가 필요`,
    )
    svgIcons[name] = null
  }
  return svgIcons[name]
}
